import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { researchPaperService } from '../services/api';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  CircularProgress,
  Tab,
  Tabs,
  Chip,
  Alert,
  Divider,
  TextField,
  MenuItem,
  Menu,
  MenuList,
  Popper,
  ClickAwayListener,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
} from '@mui/material';
import Drawer from '@mui/material/Drawer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GetAppIcon from '@mui/icons-material/GetApp';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SchoolIcon from '@mui/icons-material/School';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderIcon from '@mui/icons-material/Folder';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import ImageIcon from '@mui/icons-material/Image';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LabelIcon from '@mui/icons-material/Label';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InputAdornment from '@mui/material/InputAdornment';
import PanToolIcon from '@mui/icons-material/PanTool';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import RestoreIcon from '@mui/icons-material/Restore';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { selectionModePlugin, SelectionMode, TextSelectionIcon } from '@react-pdf-viewer/selection-mode';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { searchPlugin } from '@react-pdf-viewer/search';
import { bookmarkPlugin } from '@react-pdf-viewer/bookmark';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/selection-mode/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import '@react-pdf-viewer/search/lib/styles/index.css';
import '@react-pdf-viewer/bookmark/lib/styles/index.css';

// Self-hosted PDF.js worker (copied to public/pdf.worker.min.js to avoid unpkg latency)
const workerUrl = '/pdf.worker.min.js';

// NOTE: hardcoded content constants (abstractText, sectionTexts, fullPaperText, latexCode) removed.
// The .tex file on server is the sole source of truth.
// Paper sections with status (8 sections — standard IEEE/ACM systems-paper convention)
// Parse per-section LaTeX content from a given LaTeX string.
// Sections are discovered dynamically by scanning for `\section{...}` markers
// in the live source. Starred sections (`\section*{Acknowledgment}` etc.) are
// intentionally excluded from the parsed-section view — they remain accessible
// via the outline navigator. This mirrors Overleaf's behaviour: the outline is
// derived from the buffer, never declared in a static manifest.
function parseLatexSections(latex) {
  const result = { sections: [] };
  if (!latex) return result;

  // Title
  const titleMatch = latex.match(/\\title\{(.+?)\}/s);
  if (titleMatch) result.title = titleMatch[1].trim();

  // Keywords
  const kwStart = latex.indexOf('\\begin{IEEEkeywords}');
  const kwEnd = latex.indexOf('\\end{IEEEkeywords}');
  if (kwStart !== -1 && kwEnd !== -1) {
    result.keywords = latex.substring(kwStart, kwEnd + '\\end{IEEEkeywords}'.length).trim();
    result.keywordsText = latex.substring(kwStart + '\\begin{IEEEkeywords}'.length, kwEnd).trim();
  }

  // Abstract
  const absStart = latex.indexOf('\\begin{abstract}');
  const absEnd = latex.indexOf('\\end{abstract}');
  if (absStart !== -1 && absEnd !== -1) {
    result.abstract = latex.substring(absStart, absEnd + '\\end{abstract}'.length).trim();
  }

  // Dynamically discover every numbered \section{...} marker. Starred sections
  // are excluded (they don't lend themselves to per-section editing).
  const romans = ['I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII','XIII','XIV','XV','XVI'];
  const sectionRegex = /\\section\{([^}]+)\}/g;
  const sectionStarts = [];
  let m;
  while ((m = sectionRegex.exec(latex)) !== null) {
    sectionStarts.push({ title: m[1].trim(), index: m.index });
  }
  const endDoc = latex.indexOf('\\end{document}');
  sectionStarts.forEach((sec, idx) => {
    const bodyEnd = idx + 1 < sectionStarts.length
      ? sectionStarts[idx + 1].index
      : (endDoc !== -1 ? endDoc : latex.length);
    const sectionLatex = latex.substring(sec.index, bodyEnd).trim();
    const num = romans[idx] || String(idx + 1);
    // Stable slug key — survives most title edits unless the entire title changes.
    const key = sec.title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `section_${idx + 1}`;
    result.sections.push({ num, title: sec.title, key, latex: sectionLatex });
    // Back-compat: also expose by key on the top-level result, so legacy code
    // that does `result[key]` continues to work transparently.
    result[key] = sectionLatex;
  });

  return result;
}

// Extract subsections from a section's LaTeX
function getSubsections(sectionLatex) {
  if (!sectionLatex) return [];
  const matches = [...sectionLatex.matchAll(/\\subsection\{(.+?)\}/g)];
  return matches.map((m, i) => ({
    title: m[1],
    latex: sectionLatex.substring(m.index, i + 1 < matches.length ? matches[i + 1].index : sectionLatex.length).trim(),
  }));
}

// Fullscreen PDF rail. The controls sit in this left gutter so the page
// itself runs to the right edge of the screen and is not covered.
const PDF_FS_RAIL_PX = 48;

// The viewer paints the page on .rpv-default-layout__body and .rpv-core__inner-page,
// both locked to white by the library. The canvas is an opaque bitmap on top of
// those, so the reading surface only changes if the canvas is filtered too.
const PDF_VIEW_BACKGROUNDS = {
  light: { label: 'Light', next: 'evening', color: '#e7e9ee', filter: 'none', Icon: LightModeIcon },
  evening: { label: 'Evening', next: 'dark', color: '#2a2118', filter: 'sepia(0.7) saturate(0.72) brightness(0.88)', Icon: WbTwilightIcon },
  dark: { label: 'Dark', next: 'light', color: '#12141a', filter: 'invert(0.9) hue-rotate(180deg)', Icon: DarkModeIcon },
};

const PDF_VIEW_BG_CSS = Object.entries(PDF_VIEW_BACKGROUNDS).map(([mode, bg]) => `
[data-pdf-bg="${mode}"] .rpv-default-layout__container,
[data-pdf-bg="${mode}"] .rpv-default-layout__main,
[data-pdf-bg="${mode}"] .rpv-default-layout__body,
[data-pdf-bg="${mode}"] .rpv-core__viewer,
[data-pdf-bg="${mode}"] .rpv-core__inner-container,
[data-pdf-bg="${mode}"] .rpv-core__inner-pages,
[data-pdf-bg="${mode}"] .rpv-core__inner-page {
  background-color: ${bg.color} !important;
}
[data-pdf-bg="${mode}"] .rpv-core__canvas-layer {
  filter: ${bg.filter};
}
`).join('\n');

function PdfViewBgButton({ mode, onCycle, placement, onDark }) {
  const current = PDF_VIEW_BACKGROUNDS[mode];
  const next = PDF_VIEW_BACKGROUNDS[current.next];
  const Icon = current.Icon;
  return (
    <Tooltip title={`PDF background: ${current.label}. Click for ${next.label}`} placement={placement || 'bottom'}>
      <IconButton
        size="small"
        onClick={onCycle}
        aria-label={`PDF background ${current.label}`}
        sx={{ p: 0.5, color: onDark ? 'common.white' : 'text.secondary', '&:hover': { color: onDark ? 'common.white' : 'primary.main' } }}
      >
        <Icon sx={{ fontSize: onDark ? 20 : 16 }} />
      </IconButton>
    </Tooltip>
  );
}

// Pages are positioned with CSS transforms, and the PDF scrolls inside
// .rpv-core__inner-pages, not the window. Chrome's find bar and the viewer
// search highlight both miss that scroller unless we move it ourselves.
function scrollPdfMatchIntoView(scroller, rect) {
  if (!scroller || !rect) return;
  if (rect.width === 0 && rect.height === 0) return;
  const bounds = scroller.getBoundingClientRect();
  const pad = 72;
  if (rect.top < bounds.top + pad || rect.bottom > bounds.bottom - pad) {
    scroller.scrollTop += rect.top - bounds.top - bounds.height * 0.28;
  }
  if (rect.left < bounds.left + pad || rect.right > bounds.right - pad) {
    scroller.scrollLeft += rect.left - bounds.left - 40;
  }
}

// Read every page's text from the PDF itself. The viewer only paints a text
// layer for pages near the screen, so Chrome's find bar and the in-app search
// otherwise never see the rest of the paper.
async function readPdfPageTexts(pdfUrl) {
  GlobalWorkerOptions.workerSrc = workerUrl;
  const task = getDocument(pdfUrl);
  try {
    const doc = await task.promise;
    const texts = [];
    for (let i = 1; i <= doc.numPages; i += 1) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      let text = '';
      content.items.forEach((item) => {
        if (!item.str) return;
        text += item.str;
        text += item.hasEOL ? '\n' : ' ';
      });
      texts.push(text);
    }
    return texts;
  } finally {
    try { task.destroy(); } catch { /* the viewer keeps its own copy of the file */ }
  }
}

function stampPdfFindText(root, texts) {
  if (!root || !texts) return;
  texts.forEach((text, index) => {
    const layer = root.querySelector(`[data-testid="core__page-layer-${index}"]`);
    if (!layer) return;
    const existing = layer.querySelector('.nscat-pdf-find-text');
    if (layer.querySelector('.rpv-core__text-layer-text')) {
      existing?.remove();
      return;
    }
    if (!text) return;
    const node = existing || document.createElement('div');
    node.className = 'nscat-pdf-find-text';
    if (!existing) layer.appendChild(node);
    if (node.textContent !== text) node.textContent = text;
  });
}

function nthPdfMatchRect(root, keyword, n, matchCase, wholeWords) {
  if (!root || !keyword || !n) return null;
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const source = wholeWords ? `\\b${escaped}\\b` : escaped;
  let re;
  try {
    re = new RegExp(source, matchCase ? 'g' : 'gi');
  } catch {
    return null;
  }
  const pages = root.querySelectorAll('[data-testid^="core__page-layer-"]');
  let seen = 0;
  for (const page of pages) {
    const host = page.querySelector('.rpv-core__text-layer-text')
      ? page.querySelector('.rpv-core__text-layer')
      : page.querySelector('.nscat-pdf-find-text');
    if (!host) continue;
    const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();
    while (textNode) {
      const value = textNode.nodeValue || '';
      re.lastIndex = 0;
      let match = re.exec(value);
      while (match) {
        seen += 1;
        if (seen === n) {
          const range = document.createRange();
          range.setStart(textNode, match.index);
          range.setEnd(textNode, match.index + match[0].length);
          return range.getBoundingClientRect();
        }
        if (match.index === re.lastIndex) re.lastIndex += 1;
        match = re.exec(value);
      }
      textNode = walker.nextNode();
    }
  }
  return null;
}

function ResearchPaper() {
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // eslint-disable-line no-unused-vars
  const [expandedSection, setExpandedSection] = useState(null);
  const [abstractExpanded, setAbstractExpanded] = useState(false);
  const [keywordsExpanded, setKeywordsExpanded] = useState(false);
  const [expandedSubsections, setExpandedSubsections] = useState({});
  const [editingSubsection, setEditingSubsection] = useState(null); // { sectionIdx, subIdx }
  const [editSubText, setEditSubText] = useState('');
  const [editingSection, setEditingSection] = useState(null); // null | 'abstract' | 'latex' | sectionKey
  const [editText, setEditText] = useState('');
  const PAPER_ID = 'paper-1';

  // Server-based LaTeX state (.tex file as source of truth)
  const [serverLatex, setServerLatex] = useState(null);
  const [latexLoading, setLatexLoading] = useState(true);
  const [latexSaving, setLatexSaving] = useState(false);
  const [customLatex, setCustomLatex] = useState(''); // unsaved in-session edits
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [hasBackup, setHasBackup] = useState(false);
  const [backupModified, setBackupModified] = useState(null);
  const [revertModalOpen, setRevertModalOpen] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [outlineAnchor, setOutlineAnchor] = useState(null);
  const [subOutlineAnchor, setSubOutlineAnchor] = useState(null);
  const [subOutlineSection, setSubOutlineSection] = useState(null);
  const [jumpHighlight, setJumpHighlight] = useState(null); // {index, length}
  // Cascading-menu hover state and close-debounce timer
  const hoveringParentRef = useRef(false);
  const hoveringSubRef = useRef(false);
  const closeSubMenuTimerRef = useRef(null);
  const cancelCloseSubMenu = () => {
    if (closeSubMenuTimerRef.current) {
      clearTimeout(closeSubMenuTimerRef.current);
      closeSubMenuTimerRef.current = null;
    }
  };
  const checkAndScheduleClose = () => {
    cancelCloseSubMenu();
    if (!hoveringParentRef.current && !hoveringSubRef.current) {
      closeSubMenuTimerRef.current = setTimeout(() => {
        setSubOutlineAnchor(null);
        setSubOutlineSection(null);
      }, 250);
    }
  };
  const handleParentEnter = () => { hoveringParentRef.current = true; cancelCloseSubMenu(); };
  const handleParentLeave = () => { hoveringParentRef.current = false; checkAndScheduleClose(); };
  const handleSubEnter = () => { hoveringSubRef.current = true; cancelCloseSubMenu(); };
  const handleSubLeave = () => { hoveringSubRef.current = false; checkAndScheduleClose(); };

  // Load .tex from server on mount
  useEffect(() => {
    (async () => {
      setLatexLoading(true);
      try {
        const data = await researchPaperService.getLatex(PAPER_ID);
        setServerLatex(data.latex);
        setLastSavedTime(data.modified);
        setHasBackup(!!data.has_backup);
        setBackupModified(data.backup_modified || null);
      } catch {
        setSnackbar({ open: true, message: 'Could not load .tex from server' });
      } finally {
        setLatexLoading(false);
      }
    })();
  }, []);

  // PDF compilation state
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfCompiling, setPdfCompiling] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const pdfLatexRef = useRef(null);

  // react-pdf-viewer default layout plugin — keep the plugin for its page-area
  // chrome, but render an empty toolbar and no sidebar tabs so the inner row
  // and sidebar are suppressed. Search / bookmarks / zoom are surfaced via the
  // outer PDF Preview banner instead. Call factory directly every render — it
  // uses React hooks internally, so wrapping in useMemo violates Rules of Hooks
  // and breaks the viewer.
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar: () => <></>,
    sidebarTabs: () => [],
  });

  // react-pdf-viewer zoom plugin: gives us programmatic ZoomIn/ZoomOut/CurrentScale
  // render-props for the floating fullscreen toolbar.
  const zoomPluginInstance = zoomPlugin();
  const { CurrentScale, zoomTo } = zoomPluginInstance;
  // Track current PDF scale so the +/- buttons step in 10% increments
  // (the plugin's built-in ZoomIn/ZoomOut step through a non-linear ladder).
  const [pdfScale, setPdfScale] = useState(1.5);
  const stepZoom = useCallback((delta) => {
    const next = Math.min(5, Math.max(0.25, +(pdfScale + delta).toFixed(2)));
    zoomTo(next);
    setPdfScale(next);
  }, [pdfScale, zoomTo]);

  // react-pdf-viewer search plugin — exposes ShowSearchPopover render-prop used
  // by the outer banner's Search icon. Bookmark plugin exposes <Bookmarks /> for
  // the Drawer rendered alongside the viewer.
  const searchPluginInstance = searchPlugin();
  const { ShowSearchPopover } = searchPluginInstance;
  const bookmarkPluginInstance = bookmarkPlugin();
  const { Bookmarks } = bookmarkPluginInstance;
  const [bookmarksOpen, setBookmarksOpen] = useState(false);

  // react-pdf-viewer selection-mode plugin: provides the hand/text-selection toggle.
  // IMPORTANT: call factory directly every render — it uses React hooks internally
  // and manages its own memoization. Wrapping in useMemo breaks Rules of Hooks.
  const pdfContainerRef = useRef(null);
  const selectionModePluginInstance = selectionModePlugin({
    selectionMode: SelectionMode.Hand,
  });
  const { SwitchSelectionMode } = selectionModePluginInstance;
  // Mirror the plugin's mode in local React state so the toolbar icon re-renders on toggle.
  // (The plugin's SwitchSelectionMode render-prop reads isSelected once per parent render
  // and is not reactive on its own.)
  const [pdfSelectionMode, setPdfSelectionMode] = useState(SelectionMode.Hand);
  const selectionModeHandlersRef = useRef({ hand: null, text: null });
  const [latexHandMode, setLatexHandMode] = useState(false);
  // Which pane currently owns keyboard navigation. Updated on hover/click per UI/UX.
  const [focusedPane, setFocusedPane] = useState('pdf'); // 'latex' | 'pdf'

  // Drag-to-pan on the LaTeX view-mode scroll container when hand mode is active.
  useEffect(() => {
    if (!latexHandMode) return;
    if (editingSection === 'latex') return; // Only in view mode
    const container = latexEditorAreaRef.current;
    if (!container) return;
    let isDown = false;
    let startX = 0, startY = 0, scrollLeft = 0, scrollTop = 0;
    const onDown = (e) => {
      // Don't intercept clicks on interactive children (buttons, links, inputs)
      if (e.target.closest('button, a, input, textarea, [role="button"]')) return;
      isDown = true;
      startX = e.clientX;
      startY = e.clientY;
      scrollLeft = container.scrollLeft;
      scrollTop = container.scrollTop;
      container.style.cursor = 'grabbing';
      e.preventDefault();
    };
    const onMove = (e) => {
      if (!isDown) return;
      container.scrollLeft = scrollLeft - (e.clientX - startX);
      container.scrollTop = scrollTop - (e.clientY - startY);
    };
    const onUp = () => { isDown = false; container.style.cursor = 'grab'; };
    container.style.cursor = 'grab';
    container.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      container.style.cursor = '';
      container.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [latexHandMode, editingSection]);

  // Unified arrow-key navigation. Routes to whichever pane is focused (hover/click).
  // Inactive if user is typing in an input/textarea/contentEditable.
  useEffect(() => {
    const STEP = 80;
    const onKey = (e) => {
      // Skip if user is editing text — let the input consume the key
      const ae = document.activeElement;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) return;

      let scroller = null;
      if (focusedPane === 'pdf' && pdfSelectionMode === SelectionMode.Hand && pdfUrl) {
        scroller = pdfContainerRef.current?.querySelector('.rpv-core__inner-pages') || null;
      } else if (focusedPane === 'latex' && latexHandMode && editingSection !== 'latex') {
        scroller = latexEditorAreaRef.current;
      }
      if (!scroller) return;

      let handled = true;
      switch (e.key) {
        case 'ArrowUp':    scroller.scrollTop  -= STEP; break;
        case 'ArrowDown':  scroller.scrollTop  += STEP; break;
        case 'ArrowLeft':  scroller.scrollLeft -= STEP; break;
        case 'ArrowRight': scroller.scrollLeft += STEP; break;
        case 'PageUp':     scroller.scrollTop  -= scroller.clientHeight; break;
        case 'PageDown':   scroller.scrollTop  += scroller.clientHeight; break;
        case 'Home':       if (e.ctrlKey) { scroller.scrollTop = 0; } else handled = false; break;
        case 'End':        if (e.ctrlKey) { scroller.scrollTop = scroller.scrollHeight; } else handled = false; break;
        default:           handled = false;
      }
      if (handled) e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [focusedPane, pdfSelectionMode, latexHandMode, pdfUrl, editingSection]);

  // File management state
  const [paperFiles, setPaperFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadSubfolder, setUploadSubfolder] = useState('');
  const [downloadingFolder, setDownloadingFolder] = useState(null);
  const fileInputRef = useRef(null);
  const figuresInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  // Tracks which folder nodes are expanded in the tree view, persisted across sessions.
  const [expandedFolders, setExpandedFolders] = useState(() => {
    try {
      const raw = localStorage.getItem('nscat_paper_expanded_folders');
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* ignore */ }
    return new Set(['', 'figures']);
  });

  useEffect(() => {
    try {
      localStorage.setItem('nscat_paper_expanded_folders', JSON.stringify(Array.from(expandedFolders)));
    } catch { /* ignore */ }
  }, [expandedFolders]);

  const toggleFolder = useCallback((folderPath) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderPath)) next.delete(folderPath);
      else next.add(folderPath);
      return next;
    });
  }, []);

  const expandAllFolders = useCallback(() => {
    const allFolders = new Set(['']);
    (paperFiles || []).forEach((f) => {
      const parts = (f.path || '').split('/').filter(Boolean);
      parts.pop(); // remove filename
      let accum = '';
      parts.forEach((seg) => {
        accum = accum ? `${accum}/${seg}` : seg;
        allFolders.add(accum);
      });
    });
    setExpandedFolders(allFolders);
  }, [paperFiles]);

  const collapseAllFolders = useCallback(() => {
    setExpandedFolders(new Set(['']));
  }, []);
  const [filesPanelOpen, setFilesPanelOpen] = useState(false);
  // Files panel width in px (when open). Persisted to localStorage so the
  // user's preferred width survives reloads. Constrained to [200, 600].
  const [filesPanelWidth, setFilesPanelWidth] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem('nscat_files_panel_width'), 10);
      if (!isNaN(saved) && saved >= 200 && saved <= 600) return saved;
    } catch { /* ignore */ }
    return 280;
  });
  const [editorWidthPercent, setEditorWidthPercent] = useState(30);
  const [editorViewMode, setEditorViewMode] = useState('latex');
  const [latexFontSize, setLatexFontSize] = useState(0.78);
  const [latexFullscreen, setLatexFullscreen] = useState(false);

  // PDF fullscreen state + auto-hiding floating zoom toolbar (UX best practice:
  // controls visible by default, fade out after idle, reappear on any
  // mousemove/keypress so the document area stays unobscured during reading).
  const [pdfFullscreen, setPdfFullscreen] = useState(false);
  const [pdfViewBg, setPdfViewBg] = useState(() => {
    try {
      const saved = localStorage.getItem('nscat_paper1_pdf_bg');
      if (saved === 'light' || saved === 'evening' || saved === 'dark') return saved;
    } catch { /* private mode */ }
    return 'light';
  });
  const cyclePdfViewBg = useCallback(() => {
    setPdfViewBg((cur) => {
      const next = PDF_VIEW_BACKGROUNDS[cur].next;
      try { localStorage.setItem('nscat_paper1_pdf_bg', next); } catch { /* private mode */ }
      return next;
    });
  }, []);
  const [pdfControlsVisible, setPdfControlsVisible] = useState(true);
  const pdfControlsTimerRef = useRef(null);
  const showPdfControls = useCallback(() => {
    setPdfControlsVisible(true);
    if (pdfControlsTimerRef.current) clearTimeout(pdfControlsTimerRef.current);
    pdfControlsTimerRef.current = setTimeout(() => {
      setPdfControlsVisible(false);
    }, 3000);
  }, []);

  // Bind activity listeners + Esc-to-exit when PDF fullscreen is active.
  const pdfZoomAppliedRef = useRef(false);
  useEffect(() => {
    if (!pdfFullscreen) {
      pdfZoomAppliedRef.current = false;
      if (pdfControlsTimerRef.current) clearTimeout(pdfControlsTimerRef.current);
      setPdfControlsVisible(true);
      return;
    }
    if (!pdfZoomAppliedRef.current) {
      zoomTo(1.6);
      setPdfScale(1.6);
      pdfZoomAppliedRef.current = true;
    }
    showPdfControls();
    const onActivity = () => showPdfControls();
    const onKey = (e) => {
      showPdfControls();
      if (e.key === 'Escape') setPdfFullscreen(false);
    };
    // The PDF search plugin calls preventDefault on Ctrl+F while the pointer
    // is over the page, which blocks Chrome's find bar. Stop the shortcut
    // before that listener runs, and leave the browser default intact.
    const onFind = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'f') {
        e.stopPropagation();
      }
    };
    // Hide the editor and the rest of the app from Chrome's find bar so the
    // matches it steps through are the PDF text, then scroll the PDF scroller
    // to each match. The viewer's own search highlight is scrolled the same way.
    document.body.classList.add('nscat-pdf-fs');
    const style = document.createElement('style');
    style.setAttribute('data-nscat-pdf-fs', '');
    style.textContent = [
      'body.nscat-pdf-fs #root { visibility: hidden; }',
      'body.nscat-pdf-fs [data-pdf-fs], body.nscat-pdf-fs [data-pdf-fs] * { visibility: visible; }',
      '.nscat-pdf-find-text { position: absolute; left: 0; top: 0; width: 100%; height: auto; color: transparent; white-space: pre-wrap; word-break: break-word; font-size: 12px; line-height: 1.35; z-index: 2; pointer-events: none; }',
    ].join('\n');
    document.head.appendChild(style);
    const pdfScroller = () => pdfContainerRef.current?.querySelector('.rpv-core__inner-pages') || null;
    const onSelection = () => {
      const sel = document.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
      const node = sel.anchorNode;
      const el = node && (node.nodeType === 1 ? node : node.parentElement);
      if (!el || !(el.closest('.rpv-core__text-layer') || el.closest('.nscat-pdf-find-text'))) return;
      scrollPdfMatchIntoView(pdfScroller(), sel.getRangeAt(0).getBoundingClientRect());
    };
    const onHighlights = () => {
      const root = pdfContainerRef.current;
      const scroller = pdfScroller();
      const counter = root?.querySelector('[data-testid="search__popover-num-matches"]');
      const input = root?.querySelector('.rpv-search__popover input');
      const matchNo = parseInt((counter?.textContent || '').split('/')[0], 10);
      if (input?.value && matchNo) {
        const matchCase = !!root.querySelector('[data-testid="search__popover-match-case"]')?.checked;
        const wholeWords = !!root.querySelector('[data-testid="search__popover-whole-words"]')?.checked;
        const rect = nthPdfMatchRect(root, input.value, matchNo, matchCase, wholeWords);
        if (rect) {
          scrollPdfMatchIntoView(scroller, rect);
          return;
        }
      }
      const current = root?.querySelector('.rpv-search__highlight--current');
      if (current) scrollPdfMatchIntoView(scroller, current.getBoundingClientRect());
    };
    const highlightObserver = new MutationObserver(onHighlights);
    if (pdfContainerRef.current) {
      highlightObserver.observe(pdfContainerRef.current, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    const onSearchNav = (e) => {
      const pop = e.target.closest && e.target.closest('.rpv-search__popover');
      if (!pop) return;
      if (e.type === 'keydown' && e.key !== 'Enter') return;
      if (e.type === 'click' && !e.target.closest('button')) return;
      setTimeout(onHighlights, 60);
      setTimeout(onHighlights, 400);
    };
    window.addEventListener('mousemove', onActivity);
    window.addEventListener('keydown', onKey);
    window.addEventListener('keydown', onFind, true);
    document.addEventListener('selectionchange', onSelection);
    pdfContainerRef.current?.addEventListener('click', onSearchNav);
    pdfContainerRef.current?.addEventListener('keydown', onSearchNav);
    let textCancelled = false;
    let pageTexts = null;
    const stampObserver = new MutationObserver(() => {
      if (pageTexts) stampPdfFindText(pdfContainerRef.current, pageTexts);
    });
    if (pdfContainerRef.current) {
      stampObserver.observe(pdfContainerRef.current, { subtree: true, childList: true });
    }
    if (pdfUrl) {
      readPdfPageTexts(pdfUrl).then((texts) => {
        if (textCancelled) return;
        pageTexts = texts;
        stampPdfFindText(pdfContainerRef.current, texts);
      }).catch(() => {});
    }
    return () => {
      textCancelled = true;
      stampObserver.disconnect();
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keydown', onFind, true);
      document.removeEventListener('selectionchange', onSelection);
      pdfContainerRef.current?.removeEventListener('click', onSearchNav);
      pdfContainerRef.current?.removeEventListener('keydown', onSearchNav);
      highlightObserver.disconnect();
      document.body.classList.remove('nscat-pdf-fs');
      style.remove();
      if (pdfControlsTimerRef.current) clearTimeout(pdfControlsTimerRef.current);
    };
  }, [pdfFullscreen, showPdfControls, zoomTo, pdfUrl]);
  const [latexSearchOpen, setLatexSearchOpen] = useState(false);
  const [latexSearchTerm, setLatexSearchTerm] = useState('');
  const [latexSearchIndex, setLatexSearchIndex] = useState(0);
  const [latexSearchCount, setLatexSearchCount] = useState(0);
  const latexSearchInputRef = useRef(null);
  const latexScrollPos = useRef(0);
  const latexEditorAreaRef = useRef(null);
  const isDraggingDivider = useRef(false);
  const isDraggingFilesDivider = useRef(false);
  const editorContainerRef = useRef(null);

  const loadFiles = useCallback(async () => {
    setFilesLoading(true);
    try {
      const data = await researchPaperService.listFiles(PAPER_ID);
      setPaperFiles(data.files || []);
    } catch { setPaperFiles([]); }
    finally { setFilesLoading(false); }
  }, []);

  const handleFileUpload = useCallback(async (files, subfolder = '') => {
    if (!files || files.length === 0) return;
    setUploadingFile(true);
    try {
      for (const file of files) {
        await researchPaperService.uploadFile(PAPER_ID, file, subfolder);
      }
      setSnackbar({ open: true, message: `Uploaded ${files.length} file(s) successfully` });
      if (subfolder) {
        setExpandedFolders((prev) => new Set([...prev, subfolder]));
      }
      await loadFiles();
    } catch (err) {
      setSnackbar({ open: true, message: `Upload failed: ${err.response?.data?.error || err.message}` });
    } finally { setUploadingFile(false); }
  }, [loadFiles]);

  // Folder upload: iterates over a FileList from a <input webkitdirectory />, deriving each
  // file's target subfolder from file.webkitRelativePath, preserving the folder structure.
  // Snapshots the FileList into an array first so the iteration survives the input being reset.
  const handleFolderUpload = useCallback(async (fileList, baseSubfolder = '') => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploadingFile(true);
    const newlyExpandedFolders = new Set();
    const failed = [];
    let ok = 0;
    try {
      for (const file of files) {
        const rel = file.webkitRelativePath || file.name;
        const segments = rel.split('/').filter(Boolean);
        segments.pop(); // remove the file's own name from the path
        const fileSubfolder = baseSubfolder
          ? [baseSubfolder, ...segments].join('/')
          : segments.join('/');
        try {
          await researchPaperService.uploadFile(PAPER_ID, file, fileSubfolder);
          ok += 1;
          let accum = '';
          if (baseSubfolder) {
            accum = baseSubfolder;
            newlyExpandedFolders.add(accum);
          }
          for (const seg of segments) {
            accum = accum ? `${accum}/${seg}` : seg;
            newlyExpandedFolders.add(accum);
          }
        } catch (err) {
          failed.push({ name: rel, reason: err.response?.data?.error || err.message });
        }
      }
      if (newlyExpandedFolders.size > 0) {
        setExpandedFolders((prev) => new Set([...prev, ...newlyExpandedFolders]));
      }
      if (failed.length === 0) {
        setSnackbar({ open: true, message: `Uploaded ${ok} file(s) from folder successfully` });
      } else {
        const exts = [...new Set(failed.map((f) => (f.name.match(/\.[^.]+$/)?.[0] || '').toLowerCase()))].filter(Boolean);
        const extsNote = exts.length ? ` (rejected: ${exts.join(', ')})` : '';
        setSnackbar({
          open: true,
          message: `Uploaded ${ok} of ${files.length} file(s); ${failed.length} rejected${extsNote}`,
        });
        // eslint-disable-next-line no-console
        console.warn('[FolderUpload] Rejected files:', failed);
      }
      await loadFiles();
    } finally { setUploadingFile(false); }
  }, [loadFiles]);

  const handleDeleteFile = useCallback(async (filePath) => {
    try {
      await researchPaperService.deleteFile(PAPER_ID, filePath);
      setSnackbar({ open: true, message: `Deleted ${filePath}` });
      await loadFiles();
    } catch (err) {
      setSnackbar({ open: true, message: `Delete failed: ${err.response?.data?.detail || err.message}` });
    }
  }, [loadFiles]);

  // Delete-confirmation dialog state. UX: never delete files without confirmation;
  // show full path, mark as irreversible, default focus on Cancel.
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, filePath: null });
  const requestDelete = useCallback((filePath) => {
    setDeleteConfirm({ open: true, filePath });
  }, []);
  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, filePath: null });
  }, []);
  const confirmDelete = useCallback(async () => {
    const path = deleteConfirm.filePath;
    setDeleteConfirm({ open: false, filePath: null });
    if (path) await handleDeleteFile(path);
  }, [deleteConfirm.filePath, handleDeleteFile]);

  const handleMoveFile = useCallback(async (filePath, targetSubfolder) => {
    try {
      const res = await researchPaperService.moveFile(PAPER_ID, filePath, targetSubfolder);
      setSnackbar({ open: true, message: res.message || `Moved ${filePath}` });
      await loadFiles();
    } catch (err) {
      setSnackbar({ open: true, message: `Move failed: ${err.response?.data?.detail || err.message}` });
    }
  }, [loadFiles]);

  const handleRenameFile = useCallback(async (filePath) => {
    const currentName = filePath.includes('/') ? filePath.split('/').pop() : filePath;
    const newName = window.prompt('Rename file (current name shown):', currentName);
    if (newName === null) return;                       // user cancelled
    const trimmed = newName.trim();
    if (!trimmed || trimmed === currentName) return;     // empty or unchanged
    try {
      const res = await researchPaperService.renameFile(PAPER_ID, filePath, trimmed);
      setSnackbar({ open: true, message: res.message || `Renamed to ${trimmed}` });
      await loadFiles();
    } catch (err) {
      setSnackbar({ open: true, message: `Rename failed: ${err.response?.data?.detail || err.message}` });
    }
  }, [loadFiles]);

  // Anchor + state for the per-file "Move to…" menu
  const [moveMenuAnchor, setMoveMenuAnchor] = useState(null);
  const [moveMenuFile, setMoveMenuFile] = useState(null);
  const openMoveMenu = useCallback((event, filePath) => {
    setMoveMenuAnchor(event.currentTarget);
    setMoveMenuFile(filePath);
  }, []);
  const closeMoveMenu = useCallback(() => {
    setMoveMenuAnchor(null);
    setMoveMenuFile(null);
  }, []);

  // Distinct subfolders observed in the current file list, for use as move targets
  // AND as folder-filter chips above the file list.
  const availableSubfolders = useMemo(() => {
    const set = new Set();
    paperFiles.forEach((f) => {
      const lastSlash = (f.path || '').lastIndexOf('/');
      if (lastSlash > 0) set.add(f.path.slice(0, lastSlash));
    });
    // Always offer the canonical "figures" subfolder as a target even if empty.
    set.add('figures');
    return Array.from(set).sort();
  }, [paperFiles]);

  // Filter the displayed file list by the active subfolder selection so that
  // clicking the "root" chip shows only top-level files and clicking a
  // subfolder chip (e.g., "figures/") shows only files inside that subfolder.
  const filteredFiles = useMemo(() => {
    return paperFiles.filter((f) => {
      const path = f.path || '';
      if (uploadSubfolder === '') {
        // Root view: file paths with no slash (top-level files only)
        return !path.includes('/');
      }
      // Subfolder view: paths whose first segment matches uploadSubfolder
      return path.startsWith(uploadSubfolder + '/');
    });
  }, [paperFiles, uploadSubfolder]);

  // Build a nested folder/file tree from the flat paperFiles array.
  const fileTree = useMemo(() => {
    const root = { name: '', path: '', type: 'folder', folders: {}, files: [] };
    (paperFiles || []).forEach((f) => {
      const parts = (f.path || '').split('/').filter(Boolean);
      const fileName = parts.pop();
      let node = root;
      let accum = '';
      parts.forEach((seg) => {
        accum = accum ? `${accum}/${seg}` : seg;
        if (!node.folders[seg]) {
          node.folders[seg] = { name: seg, path: accum, type: 'folder', folders: {}, files: [] };
        }
        node = node.folders[seg];
      });
      node.files.push({ ...f, name: fileName });
    });

    const toSorted = (node) => {
      const folderArr = Object.values(node.folders).map(toSorted).sort((a, b) => a.name.localeCompare(b.name));
      const fileArr = [...node.files].sort((a, b) => a.name.localeCompare(b.name));
      return { ...node, folders: folderArr, files: fileArr };
    };
    return toSorted(root);
  }, [paperFiles]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files, uploadSubfolder);
  }, [handleFileUpload, uploadSubfolder]);

  // Serve on-disk paper.pdf for preview (no LaTeX compile).
  const loadExistingPdf = useCallback(async () => {
    if (pdfUrl || pdfCompiling) return;
    setPdfCompiling(true);
    setPdfError(null);
    try {
      const { blob } = await researchPaperService.getCompiledPdf(PAPER_ID);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      pdfLatexRef.current = customLatex || serverLatex || pdfLatexRef.current;
    } catch (err) {
      // 404 = no paper.pdf yet; leave empty "Click to Compile" state.
      if (err.response?.status !== 404) {
        const errorData = err.response?.data;
        if (errorData instanceof Blob) {
          const text = await errorData.text();
          try { setPdfError(JSON.parse(text).error); } catch { setPdfError(text); }
        } else {
          setPdfError(errorData?.error || err.message || 'Could not load paper.pdf');
        }
      }
    } finally {
      setPdfCompiling(false);
    }
  }, [customLatex, serverLatex, pdfUrl, pdfCompiling]);

  // Reload on-disk paper.pdf into the preview. Does not run pdflatex.
  const refetchPdf = useCallback(async () => {
    if (pdfCompiling) return;
    setPdfCompiling(true);
    setPdfError(null);
    try {
      const { blob } = await researchPaperService.getCompiledPdf(PAPER_ID, { cacheBust: true });
      const url = URL.createObjectURL(blob);
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData instanceof Blob) {
        const text = await errorData.text();
        try { setPdfError(JSON.parse(text).error); } catch { setPdfError(text); }
      } else {
        setPdfError(errorData?.error || err.message || 'Could not load paper.pdf');
      }
    } finally {
      setPdfCompiling(false);
    }
  }, [pdfCompiling]);

  const compilePdf = useCallback(async (forceRecompile = false, explicitLatex = null) => {
    let latexToCompile = explicitLatex;
    // Option A: explicit Recompile (forceRecompile=true, no explicit latex passed in)
    // pulls a fresh copy of the .tex from the server before compiling. This
    // ensures the PDF reflects on-disk changes (e.g., made by a collaborator
    // or via direct file edit) and discards any unsaved editor edits.
    if (latexToCompile === null && forceRecompile) {
      try {
        const fresh = await researchPaperService.getLatex(PAPER_ID);
        const freshTex = fresh?.latex || '';
        setServerLatex(freshTex);
        setCustomLatex(''); // drop unsaved in-editor edits
        latexToCompile = freshTex;
      } catch (err) {
        // If the refetch fails, fall back to current editor state so the
        // user can still attempt a compile rather than getting stuck.
        latexToCompile = customLatex || serverLatex || '';
      }
    }
    if (latexToCompile === null) latexToCompile = customLatex || serverLatex || '';
    if (!forceRecompile && pdfUrl && pdfLatexRef.current === latexToCompile) return;
    setPdfCompiling(true);
    setPdfError(null);
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
    try {
      const blob = await researchPaperService.compileLatex(PAPER_ID, latexToCompile);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      pdfLatexRef.current = latexToCompile;
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData instanceof Blob) {
        const text = await errorData.text();
        try { setPdfError(JSON.parse(text).error); } catch { setPdfError(text); }
      } else {
        setPdfError(errorData?.error || err.message || 'Compilation failed');
      }
    } finally {
      setPdfCompiling(false);
    }
  }, [customLatex, serverLatex, pdfUrl]);

  // Load files + existing paper.pdf on page load / refresh (no recompile).
  // Save and Recompile still call compilePdf and rewrite paper.pdf.
  useEffect(() => {
    if (activeTab === 0) {
      loadFiles();
      if (!pdfUrl && !pdfCompiling) {
        loadExistingPdf();
      }
    }
  }, [activeTab, loadFiles, pdfUrl, pdfCompiling, loadExistingPdf]);

  // Draggable divider between LaTeX editor and PDF preview, and the
  // separate divider on the right edge of the Files panel.
  const [isDraggingActive, setIsDraggingActive] = useState(false);
  useEffect(() => {
    const handleMouseMove = (e) => {
      // Editor/PDF divider (percentage of inner container)
      if (isDraggingDivider.current && editorContainerRef.current) {
        e.preventDefault();
        const container = editorContainerRef.current;
        const rect = container.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const pct = Math.min(Math.max((offsetX / rect.width) * 100, 15), 85);
        setEditorWidthPercent(pct);
        return;
      }
      // Files panel divider — width in absolute pixels, clamped to [200, 600]
      if (isDraggingFilesDivider.current) {
        e.preventDefault();
        const next = Math.min(Math.max(e.clientX, 200), 600);
        setFilesPanelWidth(next);
      }
    };
    const handleMouseUp = () => {
      if (isDraggingDivider.current) {
        isDraggingDivider.current = false;
        setIsDraggingActive(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
      if (isDraggingFilesDivider.current) {
        isDraggingFilesDivider.current = false;
        setIsDraggingActive(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        try { localStorage.setItem('nscat_files_panel_width', String(filesPanelWidth)); } catch { /* ignore */ }
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [filesPanelWidth]);

  // Source of truth chain: unsaved edits → server .tex
  const currentLatex = customLatex || serverLatex || '';
  const isDirty = !!(customLatex && customLatex !== serverLatex);
  const currentLatexSections = useMemo(() => parseLatexSections(currentLatex), [currentLatex]);

  // Outline items derived from current LaTeX (Title, Abstract, Keywords, all \section{} markers)
  const outlineItems = useMemo(() => {
    if (!currentLatex) return [];
    const items = [];
    const titleMatch = currentLatex.match(/\\title\{/);
    if (titleMatch) items.push({ label: 'Title', index: titleMatch.index, length: 7 });
    const absIdx = currentLatex.indexOf('\\begin{abstract}');
    if (absIdx !== -1) items.push({ label: 'Abstract', index: absIdx, length: 16 });
    const kwIdx = currentLatex.indexOf('\\begin{IEEEkeywords}');
    if (kwIdx !== -1) items.push({ label: 'Keywords', index: kwIdx, length: 20 });

    const sectionRegex = /\\section\*?\{([^}]+)\}/g;
    const romans = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

    // First pass: collect all section matches with their positions
    const sectionMatches = [];
    let m;
    while ((m = sectionRegex.exec(currentLatex)) !== null) {
      sectionMatches.push({ title: m[1], index: m.index, length: m[0].length });
    }

    // Second pass: for each section, scan its body for subsections
    sectionMatches.forEach((sec, idx) => {
      const num = romans[idx] || String(idx + 1);
      const bodyStart = sec.index + sec.length;
      const bodyEnd = idx + 1 < sectionMatches.length ? sectionMatches[idx + 1].index : currentLatex.length;
      const body = currentLatex.substring(bodyStart, bodyEnd);

      const subsections = [];
      const subRegex = /\\subsection\*?\{([^}]+)\}/g;
      let sm;
      let subI = 0;
      while ((sm = subRegex.exec(body)) !== null) {
        const letter = String.fromCharCode(65 + subI); // A, B, C, ...
        subsections.push({
          label: `${num}-${letter}. ${sm[1]}`,
          index: bodyStart + sm.index,
          length: sm[0].length,
        });
        subI++;
      }

      items.push({
        label: `${num}. ${sec.title}`,
        index: sec.index,
        length: sec.length,
        subsections,
      });
    });
    return items;
  }, [currentLatex]);

  // Jump editor scroll to a source line position (used by outline menu).
  // Sets the highlight first so a <mark> renders at the target offset; then
  // measures mark's on-screen position with getBoundingClientRect() — this
  // accounts for word-wrap in `pre-wrap` rendering. Line-number math is the fallback.
  const jumpToSourceLine = useCallback((targetIndex, length) => {
    setOutlineAnchor(null);
    setJumpHighlight({ index: targetIndex, length });
    setTimeout(() => {
      const container = latexEditorAreaRef.current;
      if (!container) return;
      const mark = container.querySelector('mark');
      let scrollPos;
      if (mark) {
        const rect = mark.getBoundingClientRect();
        const cRect = container.getBoundingClientRect();
        scrollPos = container.scrollTop + (rect.top - cRect.top) - 24;
      } else {
        const lineNumber = currentLatex.substring(0, targetIndex).split('\n').length;
        const lineHeight = latexFontSize * 16 * 1.6;
        scrollPos = (lineNumber - 1) * lineHeight - 24;
      }
      container.scrollTo({ top: Math.max(0, scrollPos), behavior: 'smooth' });
      setTimeout(() => setJumpHighlight(null), 2200);
    }, 80);
  }, [currentLatex, latexFontSize]);

  // Extract plain text from a LaTeX section (strip commands for Paper Content display)
  const stripLatex = (latex) => {
    if (!latex) return '';
    return latex
      .replace(/\\(sub)?section\{[^}]*\}/g, '')
      .replace(/\\begin\{abstract\}|\\end\{abstract\}/g, '')
      .replace(/\\begin\{(itemize|enumerate)\}|\\end\{(itemize|enumerate)\}/g, '')
      .replace(/\\item\s*/g, '— ')
      .replace(/\\textbf\{([^}]*)\}/g, '$1')
      .replace(/\\textit\{([^}]*)\}/g, '$1')
      .replace(/\\emph\{([^}]*)\}/g, '$1')
      .replace(/\\cite\{([^}]*)\}/g, '[$1]')
      .replace(/\\label\{[^}]*\}/g, '')
      .replace(/\\ref\{[^}]*\}/g, '')
      .replace(/\$\\times\$/g, '×')
      .replace(/\$\\geq\$/g, '≥')
      .replace(/\$\\leq\$/g, '≤')
      .replace(/\$<\$/g, '<')
      .replace(/\$>\$/g, '>')
      .replace(/\$([^$]+)\$/g, '$1')
      .replace(/\\\\/g, '')
      .replace(/\\&/g, '&')
      .replace(/\\_/g, '_')
      .replace(/\\%/g, '%')
      .replace(/\\hyphenation\{[^}]*\}/g, '')
      .replace(/%--.*/g, '')
      .replace(/%\s*=+.*/g, '')
      .replace(/%\s+.*/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const getAbstractText = () => stripLatex(currentLatexSections.abstract) || '';
  const getSectionText = (key) => stripLatex(currentLatexSections[key]) || '';

  const captureLatexScroll = () => {
    const el = latexEditorAreaRef.current;
    if (el) latexScrollPos.current = el.scrollTop;
  };

  // Close search while preserving current scroll position.
  // Without this, swapping from the highlighted contentEditable div back to
  // the plain TextField/pre re-mounts a new DOM element with scrollTop=0.
  const closeLatexSearch = useCallback(() => {
    captureLatexScroll();
    setLatexSearchOpen(false);
    setLatexSearchTerm('');
    setLatexSearchCount(0);
    setLatexSearchIndex(0);
    // Restore scroll after React swaps the rendering component on next frame.
    requestAnimationFrame(() => {
      const el = latexEditorAreaRef.current;
      if (el) el.scrollTop = latexScrollPos.current;
    });
  }, []);

  useEffect(() => {
    const el = latexEditorAreaRef.current;
    if (!el || editorViewMode !== 'latex') return;
    requestAnimationFrame(() => {
      el.scrollTop = latexScrollPos.current;
    });
  }, [editingSection, editorViewMode]);

  const startEditing = (key, text) => {
    if (key === 'latex') captureLatexScroll();
    setEditingSection(key);
    setEditText(text);
  };

  const saveToServer = useCallback(async (newLatex) => {
    setLatexSaving(true);
    try {
      const result = await researchPaperService.saveLatex(PAPER_ID, newLatex);
      setServerLatex(newLatex);
      setCustomLatex('');
      setLastSavedTime(result.modified);
      setHasBackup(!!result.has_backup);
      return true;
    } catch (err) {
      setSnackbar({ open: true, message: `Save failed: ${err.response?.data?.error || err.message}` });
      return false;
    } finally {
      setLatexSaving(false);
    }
  }, []);

  const handleRevert = useCallback(async () => {
    setReverting(true);
    try {
      const result = await researchPaperService.revertLatex(PAPER_ID);
      setServerLatex(result.latex);
      setCustomLatex('');
      setLastSavedTime(result.modified);
      setHasBackup(!!result.has_backup);
      setRevertModalOpen(false);
      setSnackbar({ open: true, message: 'Reverted — recompiling PDF…' });
      // Auto-compile with reverted content
      compilePdf(true, result.latex);
    } catch (err) {
      setSnackbar({ open: true, message: `Revert failed: ${err.response?.data?.detail || err.message}` });
    } finally {
      setReverting(false);
    }
  }, [compilePdf]);

  const saveEdit = async () => {
    if (!editingSection) return;

    let newLatex;
    if (editingSection === 'latex') {
      newLatex = editText;
    } else {
      const sectionKey = editingSection;
      const oldSection = currentLatexSections[sectionKey];
      if (oldSection) {
        newLatex = currentLatex.replace(oldSection, editText);
      }
    }

    if (newLatex) {
      captureLatexScroll();
      const saved = await saveToServer(newLatex);
      if (saved) {
        setSnackbar({ open: true, message: 'Saved — recompiling PDF\u2026' });
        // Auto-compile PDF with freshly saved latex
        compilePdf(true, newLatex);
      } else {
        setSnackbar({ open: true, message: 'Save failed — changes kept locally' });
        setCustomLatex(newLatex); // keep as unsaved edit
      }
    }
    setEditingSection(null);
    setEditText('');
  };

  const cancelEdit = () => {
    captureLatexScroll();
    setEditingSection(null);
    setEditText('');
  };

  // --- LaTeX search ---
  const latexSearchHighlight = useCallback((text, term) => {
    if (!term || !text) return { highlighted: text, count: 0 };
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedTerm})`, 'gi');
    const parts = text.split(regex);
    let matchIdx = 0;
    let count = 0;
    const highlighted = parts.map((part, i) => {
      if (regex.test(part)) {
        regex.lastIndex = 0; // reset after test
        const thisIdx = count;
        count++;
        const isActive = thisIdx === latexSearchIndex;
        return (
          <span
            key={i}
            data-search-match={thisIdx}
            style={{
              backgroundColor: isActive ? '#f5a623' : '#fef08a',
              color: '#000',
              borderRadius: 2,
              padding: '0 1px',
              outline: isActive ? '2px solid #f5a623' : 'none',
            }}
          >
            {part}
          </span>
        );
      }
      return part;
    });
    return { highlighted, count };
  }, [latexSearchIndex]);

  // Scroll to the Nth match in edit mode
  const selectMatchInTextarea = useCallback((source, term, matchIndex) => {
    if (!source || !term) return;
    const container = latexEditorAreaRef.current;
    if (!container) return;
    setTimeout(() => {
      const match = container.querySelector(`[data-search-match="${matchIndex}"]`);
      if (match) {
        match.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  }, []);

  const scrollToSearchMatch = useCallback((index) => {
    if (editingSection === 'latex') {
      // In edit mode: select text in textarea
      selectMatchInTextarea(editText, latexSearchTerm, index);
    } else {
      // In view mode: scroll to highlighted span
      const container = latexEditorAreaRef.current;
      if (!container) return;
      const match = container.querySelector(`[data-search-match="${index}"]`);
      if (match) {
        match.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [editingSection, editText, latexSearchTerm, selectMatchInTextarea]);

  const handleLatexSearch = useCallback((term) => {
    setLatexSearchTerm(term);
    if (!term) {
      setLatexSearchCount(0);
      setLatexSearchIndex(0);
      return;
    }
    const source = editingSection === 'latex' ? editText : currentLatex;
    if (!source) return;
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const matches = source.match(new RegExp(escapedTerm, 'gi'));
    const count = matches ? matches.length : 0;
    setLatexSearchCount(count);
    setLatexSearchIndex(0);
    if (count > 0) {
      if (editingSection === 'latex') {
        setTimeout(() => selectMatchInTextarea(source, term, 0), 100);
      } else {
        setTimeout(() => scrollToSearchMatch(0), 100);
      }
    }
  }, [editingSection, editText, currentLatex, scrollToSearchMatch, selectMatchInTextarea]);

  const handleSearchNav = useCallback((direction) => {
    if (latexSearchCount === 0) return;
    const newIndex = direction === 'next'
      ? (latexSearchIndex + 1) % latexSearchCount
      : (latexSearchIndex - 1 + latexSearchCount) % latexSearchCount;
    setLatexSearchIndex(newIndex);
    if (editingSection === 'latex') {
      setTimeout(() => selectMatchInTextarea(editText, latexSearchTerm, newIndex), 50);
    } else {
      setTimeout(() => scrollToSearchMatch(newIndex), 50);
    }
  }, [latexSearchCount, latexSearchIndex, scrollToSearchMatch, editingSection, editText, latexSearchTerm, selectMatchInTextarea]);

  // Ctrl+F keyboard shortcut for LaTeX search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && editorViewMode === 'latex') {
        // Only capture if the LaTeX editor area is visible / in focus area
        const editorArea = latexEditorAreaRef.current;
        if (editorArea && (editorArea.contains(document.activeElement) || editorArea.parentElement?.contains(document.activeElement))) {
          e.preventDefault();
          setLatexSearchOpen(true);
          setTimeout(() => latexSearchInputRef.current?.focus(), 50);
        }
      }
      if (e.key === 'Escape' && latexSearchOpen) {
        closeLatexSearch();
      } else if (e.key === 'Escape' && latexFullscreen) {
        setLatexFullscreen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editorViewMode, latexSearchOpen, closeLatexSearch, latexFullscreen]);

  const toggleSubsection = (sectionIdx, subIdx) => {
    const key = `${sectionIdx}-${subIdx}`;
    setExpandedSubsections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const startEditingSubsection = (sectionIdx, subIdx, latex) => {
    setEditingSubsection({ sectionIdx, subIdx });
    setEditSubText(latex);
  };

  const saveSubsectionEdit = async (sectionIdx, subIdx) => {
    const section = currentLatexSections.sections?.[sectionIdx];
    const sectionLatex = section?.latex;
    if (!sectionLatex) return;
    const subs = getSubsections(sectionLatex);
    const oldSubLatex = subs[subIdx]?.latex;
    if (!oldSubLatex) return;
    const newSectionLatex = sectionLatex.replace(oldSubLatex, editSubText);
    const newLatex = currentLatex.replace(sectionLatex, newSectionLatex);
    const saved = await saveToServer(newLatex);
    setSnackbar({ open: true, message: saved ? 'Subsection saved' : 'Save failed — changes kept locally' });
    if (!saved) setCustomLatex(newLatex);
    setEditingSubsection(null);
    setEditSubText('');
  };

  const cancelSubsectionEdit = () => {
    setEditingSubsection(null);
    setEditSubText('');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSnackbar({ open: true, message: 'Research paper refreshed' });
    } catch (err) {
      console.error('Error refreshing:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCopy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({ open: true, message: `${label} copied to clipboard` });
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setSnackbar({ open: true, message: `${label} copied to clipboard` });
      } catch (fallbackErr) {
        console.error('Copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleDownloadPdf = () => {
    const printWindow = window.open('', '_blank');
    const allSections = [
      { title: 'Abstract', text: getAbstractText() },
      ...(currentLatexSections.sections || []).map((s) => ({
        title: `${s.num}. ${s.title}`,
        text: stripLatex(s.latex),
      })),
    ];
    const sectionsHtml = allSections.map(s => {
      const escaped = s.text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<div class="section-title">${s.title}</div><div class="section-body"><p>${escaped.split('\\n\\n').join('</p><p>')}</p></div>`;
    }).join('');
    printWindow.document.write(`
      <html>
        <head>
          <title>NSCAT Research Paper — Outline</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&family=Merriweather:wght@400;700&display=swap');
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Merriweather', 'Georgia', serif; color: #1a1a1a; padding: 56px; line-height: 1.9; font-size: 11.5px; }
            .header { text-align: center; margin-bottom: 28px; padding-bottom: 16px; border-bottom: 2px solid #1565c0; }
            .header h1 { font-family: 'Roboto', sans-serif; font-size: 18px; font-weight: 700; margin-bottom: 10px; line-height: 1.4; }
            .header .authors { font-size: 12px; color: #333; font-style: italic; }
            .header .affiliation { font-size: 10px; color: #666; }
            .section-title { font-family: 'Roboto', sans-serif; font-size: 13px; font-weight: 700; color: #1565c0; margin: 18px 0 8px; text-transform: uppercase; letter-spacing: 0.5px; }
            .section-body { text-align: justify; hyphens: auto; }
            .section-body p { margin-bottom: 10px; }
            .keywords { margin-top: 14px; padding-top: 10px; border-top: 1px solid #e0e0e0; }
            .keywords strong { font-family: 'Roboto', sans-serif; font-size: 10px; text-transform: uppercase; }
            .keywords span { font-size: 10px; color: #444; }
            .footer { margin-top: 24px; padding-top: 8px; border-top: 1px solid #ccc; text-align: center; font-size: 9px; color: #999; font-family: 'Roboto', sans-serif; }
            @media print { body { padding: 40px 46px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>NSCAT: A Multi-Vendor Firewall Compliance Framework with Triple-Strategy NIST SP 800-53 Mapping, Explainable AI, and SOC Orchestration</h1>
            <p class="authors">Naeem Amjad, Attiq Ahmad, Narmeen Shafqat, Imran Rashid</p>
            <p class="affiliation">Department of Information Security, National University of Sciences & Technology (NUST), Islamabad, Pakistan</p>
          </div>
          ${sectionsHtml}
          <div class="keywords">
            <strong>Keywords: </strong>
            <span>Network Security, Configuration Auditing, AI, LLMs, Multi-Vendor Firewall, NIST SP 800-53 Rev 5, RAG, CDM, SOC, Compliance Automation</span>
          </div>
          <div class="footer">NSCAT Research Paper — Outline | Department of Information Security, NUST</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.print(); };
    setSnackbar({ open: true, message: 'Full paper opened for download' });
  };

  const renderTreeNode = (node, depth = 0) => {
    const isRoot = node.path === '';
    const isOpen = expandedFolders.has(node.path);
    const indent = depth * 12;

    return (
      <React.Fragment key={node.path || '__root__'}>
        {!isRoot && (
          <Box
            onClick={() => toggleFolder(node.path)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              py: 0.5, pr: 0.5, pl: `${indent + 4}px`,
              borderRadius: 1, cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {isOpen
              ? <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              : <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
            <FolderIcon sx={{ fontSize: 16, color: 'warning.light' }} />
            <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {node.name}{' '}
              <Typography component="span" variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', fontWeight: 400 }}>
                ({node.files.length + node.folders.length})
              </Typography>
            </Typography>
            <Tooltip title={`Download "${node.name}" folder as ZIP`}>
              <IconButton
                size="small"
                disabled={downloadingFolder === node.path}
                onClick={async (e) => {
                  e.stopPropagation();
                  setDownloadingFolder(node.path);
                  try {
                    const blob = await researchPaperService.downloadFolder(PAPER_ID, node.path);
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `NSCAT_${PAPER_ID === 'paper-1' ? 'Paper_1' : 'Paper_2'}_${node.name}.zip`;
                    document.body.appendChild(a); a.click();
                    window.URL.revokeObjectURL(url); document.body.removeChild(a);
                  } catch {
                    setSnackbar({ open: true, message: `Failed to download "${node.name}" folder` });
                  } finally {
                    setDownloadingFolder(null);
                  }
                }}
                sx={{ p: 0.25 }}
              >
                {downloadingFolder === node.path
                  ? <CircularProgress size={12} />
                  : <FolderZipIcon sx={{ fontSize: 12, color: 'primary.main' }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Set as upload target">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setUploadSubfolder(node.path); }} sx={{ p: 0.25 }}>
                <DriveFileMoveIcon sx={{ fontSize: 12, color: uploadSubfolder === node.path ? 'primary.main' : 'text.disabled' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Collapse in={isRoot || isOpen} timeout="auto" unmountOnExit={!isRoot}>
          {node.folders.map((child) => renderTreeNode(child, isRoot ? 0 : depth + 1))}
          {node.files.map((f) => (
            <Box
              key={f.path}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                py: 0.5, pr: 0.5, pl: `${(isRoot ? 0 : depth + 1) * 12 + 22}px`,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <InsertDriveFileIcon sx={{ fontSize: 16, color: (f.name || f.filename || f.path || '').toLowerCase().endsWith('.pdf') ? 'error.main' : (f.name || f.filename || f.path || '').toLowerCase().endsWith('.tex') ? 'success.main' : f.type === 'image' ? 'warning.main' : f.type === 'bibliography' ? 'success.main' : f.type === 'latex' ? 'primary.light' : 'info.main' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }} title={f.path}>
                  {f.name || f.filename}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                  {f.size < 1024 ? `${f.size} B` : f.size < 1024 * 1024 ? `${(f.size / 1024).toFixed(1)} KB` : `${(f.size / (1024 * 1024)).toFixed(1)} MB`}
                </Typography>
              </Box>
              <Tooltip title="Download">
                <IconButton size="small" onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const blob = await researchPaperService.downloadFile(PAPER_ID, f.path);
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = f.filename;
                    document.body.appendChild(a); a.click();
                    window.URL.revokeObjectURL(url); document.body.removeChild(a);
                  } catch { setSnackbar({ open: true, message: 'Download failed' }); }
                }} sx={{ p: 0.25 }}>
                  <GetAppIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rename…">
                <IconButton size="small" onClick={() => handleRenameFile(f.path)} sx={{ p: 0.25 }}>
                  <DriveFileRenameOutlineIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Move to subfolder…">
                <IconButton size="small" onClick={(e) => openMoveMenu(e, f.path)} sx={{ p: 0.25 }}>
                  <DriveFileMoveIcon sx={{ fontSize: 14, color: 'info.main' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => requestDelete(f.path)} sx={{ p: 0.25 }}>
                  <DeleteIcon sx={{ fontSize: 14, color: 'error.main' }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Collapse>
      </React.Fragment>
    );
  };

  const toggleSection = (idx) => {
    setExpandedSection(expandedSection === idx ? null : idx);
  };

  return (
    <Box sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Status bar - only show when loading, saving, or warnings */}
      {(latexLoading || latexSaving || isDirty || (!serverLatex && !latexLoading)) && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.25 }}>
        {latexLoading && <><CircularProgress size={14} /><Typography variant="caption" color="text.secondary">Loading .tex...</Typography></>}
        {latexSaving && <><CircularProgress size={14} /><Typography variant="caption" color="text.secondary">Saving...</Typography></>}
        {isDirty && <Chip label="Unsaved changes" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />}
        {!serverLatex && !latexLoading && <Chip label="No .tex file on server" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />}
      </Box>
      )}

      {/* Editor */}
      <Box sx={{ display: 'flex', gap: 0, flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {/* Left: File Manager Panel — draggable right edge resizes width when open */}
          <Paper sx={{
            width: filesPanelOpen ? filesPanelWidth : 48,
            minWidth: filesPanelOpen ? 200 : 48,
            maxWidth: filesPanelOpen ? 600 : 48,
            p: filesPanelOpen ? 2 : 0.5,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: isDraggingActive ? 'none' : 'width 0.3s ease',
            borderRight: 'none',
            borderRadius: 0,
            position: 'relative',
            flexShrink: 0,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: filesPanelOpen ? 'space-between' : 'center', mb: filesPanelOpen ? 1 : 0 }}>
              {filesPanelOpen && (
                <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderIcon fontSize="small" color="primary" /> Project Files
                </Typography>
              )}
              <Tooltip title={filesPanelOpen ? 'Collapse panel' : 'Expand panel'}>
                <IconButton size="small" onClick={() => setFilesPanelOpen(!filesPanelOpen)} sx={{ p: 0.5 }}>
                  {filesPanelOpen ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>

            {filesPanelOpen && (<>
            {/* Upload area */}
            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: '2px dashed',
                borderColor: dragOver ? 'primary.main' : 'divider',
                borderRadius: 1,
                p: 1.5,
                mb: 1,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: dragOver ? 'action.hover' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
              <Typography variant="caption" display="block" color="text.secondary">
                {uploadingFile ? 'Uploading...' : 'Drop files or click to upload'}
              </Typography>
              <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                .bib .png .jpg .pdf .eps .cls .sty .tex
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".bib,.bst,.cls,.sty,.tex,.png,.jpg,.jpeg,.pdf,.eps,.svg,.gif,.tif,.tiff"
                style={{ display: 'none' }}
                onChange={(e) => { handleFileUpload(e.target.files, uploadSubfolder); e.target.value = ''; }}
              />
            </Box>

            {/* Direct upload to figures/ (image-only, bypasses subfolder selector) */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<ImageIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => { e.stopPropagation(); figuresInputRef.current?.click(); }}
              disabled={uploadingFile}
              sx={{ textTransform: 'none', fontSize: '0.72rem', mb: 1, justifyContent: 'flex-start', py: 0.5 }}
              fullWidth
            >
              {uploadingFile ? 'Uploading…' : 'Upload images to figures/'}
            </Button>
            <input
              ref={figuresInputRef}
              type="file"
              multiple
              accept=".png,.jpg,.jpeg,.gif,.svg,.pdf,.eps,.tif,.tiff,image/*"
              style={{ display: 'none' }}
              onChange={(e) => { handleFileUpload(e.target.files, 'figures'); e.target.value = ''; }}
            />

            {/* Folder upload: preserves nested directory structure via webkitdirectory. */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<FolderIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => { e.stopPropagation(); folderInputRef.current?.click(); }}
              disabled={uploadingFile}
              sx={{ textTransform: 'none', fontSize: '0.72rem', mb: 1, justifyContent: 'flex-start', py: 0.5 }}
              fullWidth
            >
              {uploadingFile ? 'Uploading…' : 'Upload folder'}
            </Button>
            <input
              ref={folderInputRef}
              type="file"
              multiple
              webkitdirectory=""
              directory=""
              mozdirectory=""
              style={{ display: 'none' }}
              onChange={(e) => { handleFolderUpload(e.target.files, uploadSubfolder); e.target.value = ''; }}
            />

            {/* Folder filter chips (also act as upload-target selector for the drag/drop area).
                Click "root" to see only top-level files; click a subfolder to see only its files. */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
              <Chip
                label="root"
                size="small"
                icon={<FolderIcon sx={{ fontSize: 14 }} />}
                variant={uploadSubfolder === '' ? 'filled' : 'outlined'}
                color={uploadSubfolder === '' ? 'primary' : 'default'}
                onClick={() => setUploadSubfolder('')}
                sx={{ fontSize: '0.7rem' }}
              />
              {availableSubfolders.map((sub) => (
                <Chip
                  key={sub}
                  label={`${sub}/`}
                  size="small"
                  icon={<FolderIcon sx={{ fontSize: 14 }} />}
                  variant={uploadSubfolder === sub ? 'filled' : 'outlined'}
                  color={uploadSubfolder === sub ? 'primary' : 'default'}
                  onClick={() => {
                    setUploadSubfolder(sub);
                    setExpandedFolders((prev) => new Set([...prev, sub]));
                  }}
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
            </Box>

            {/* File list - tree view */}
            <Box sx={{ flex: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 } }}>
              {/* Expand-all / Collapse-all controls */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mb: 0.5 }}>
                <Tooltip title="Expand all folders">
                  <IconButton size="small" onClick={expandAllFolders} sx={{ p: 0.25 }}>
                    <UnfoldMoreIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Collapse all folders">
                  <IconButton size="small" onClick={collapseAllFolders} sx={{ p: 0.25 }}>
                    <UnfoldLessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              </Box>
              {filesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
              ) : paperFiles.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3, fontSize: '0.8rem' }}>
                  No files uploaded yet
                </Typography>
              ) : (
                uploadSubfolder === ''
                  ? renderTreeNode(fileTree, 0)
                  : (() => {
                      const findFolder = (node, targetPath) => {
                        if (node.path === targetPath) return node;
                        for (const folder of node.folders) {
                          const found = findFolder(folder, targetPath);
                          if (found) return found;
                        }
                        return null;
                      };
                      const target = findFolder(fileTree, uploadSubfolder);
                      return target ? renderTreeNode(target, 0) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3, fontSize: '0.8rem' }}>
                          No files in {uploadSubfolder}/
                        </Typography>
                      );
                    })()
              )}
            </Box>

            {/* Per-file "Move to subfolder" menu */}
            <Menu
              anchorEl={moveMenuAnchor}
              open={Boolean(moveMenuAnchor)}
              onClose={closeMoveMenu}
              MenuListProps={{ dense: true }}
            >
              <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600 }}>
                Move to…
              </Typography>
              <Divider />
              <MenuItem
                onClick={() => {
                  if (moveMenuFile) handleMoveFile(moveMenuFile, '');
                  closeMoveMenu();
                }}
                sx={{ fontSize: '0.8rem' }}
              >
                <FolderIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                root (paper folder)
              </MenuItem>
              {availableSubfolders.map((sub) => (
                <MenuItem
                  key={sub}
                  onClick={() => {
                    if (moveMenuFile) handleMoveFile(moveMenuFile, sub);
                    closeMoveMenu();
                  }}
                  sx={{ fontSize: '0.8rem' }}
                >
                  <FolderIcon sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
                  {sub}/
                </MenuItem>
              ))}
              <Divider />
              <MenuItem
                onClick={() => {
                  const name = window.prompt('New subfolder name (e.g. "tables", "appendix"):', '');
                  const trimmed = (name || '').trim().replace(/^\/+|\/+$/g, '');
                  if (trimmed && moveMenuFile) handleMoveFile(moveMenuFile, trimmed);
                  closeMoveMenu();
                }}
                sx={{ fontSize: '0.8rem', color: 'primary.main' }}
              >
                <CreateNewFolderIcon sx={{ fontSize: 16, mr: 1 }} />
                New subfolder…
              </MenuItem>
            </Menu>

            {/* Refresh files */}
            <Button size="small" onClick={loadFiles} startIcon={<RefreshIcon sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none', mt: 1, fontSize: '0.75rem' }}>
              Refresh Files
            </Button>
            </>)}
          </Paper>

          {/* Draggable divider for the Files panel right edge — only visible when panel is open */}
          {filesPanelOpen && (
            <Box
              onMouseDown={(e) => {
                e.preventDefault();
                isDraggingFilesDivider.current = true;
                setIsDraggingActive(true);
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
              }}
              sx={{
                width: 6,
                cursor: 'col-resize',
                position: 'relative',
                flexShrink: 0,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0, bottom: 0, left: '50%',
                  transform: 'translateX(-50%)',
                  width: 2,
                  bgcolor: 'divider',
                  transition: 'background-color 0.15s, width 0.15s',
                },
                '&:hover::before': {
                  width: 4,
                  bgcolor: 'primary.main',
                },
              }}
            />
          )}

          {/* Editor + PDF resizable split */}
          <Box ref={editorContainerRef} sx={{ flex: 1, display: 'flex', minWidth: 0, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
          {/* Center: LaTeX Editor / Paper Content */}
          <Box sx={latexFullscreen
            ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', zIndex: 1300, display: 'flex', flexDirection: 'column', bgcolor: 'background.default', borderRight: 'none' }
            : { width: `${editorWidthPercent}%`, minWidth: 0, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRight: 'none' }
          }>
            {/* Editor toolbar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0, position: 'sticky', top: 0, zIndex: 10 }}>
              <Box />
              {editorViewMode === 'latex' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Decrease font size">
                    <span>
                      <IconButton size="small" onClick={() => setLatexFontSize(prev => Math.max(0.5, +(prev - 0.06).toFixed(2)))} disabled={latexFontSize <= 0.5}>
                        <RemoveIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', minWidth: 20, textAlign: 'center', color: 'text.secondary', userSelect: 'none' }}>
                    {Math.round(latexFontSize * 16)}
                  </Typography>
                  <Tooltip title="Increase font size">
                    <span>
                      <IconButton size="small" onClick={() => setLatexFontSize(prev => Math.min(1.5, +(prev + 0.06).toFixed(2)))} disabled={latexFontSize >= 1.5}>
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Jump to section in LaTeX source">
                    <span>
                      <IconButton
                        size="small"
                        onClick={(e) => setOutlineAnchor(e.currentTarget)}
                        disabled={outlineItems.length === 0}
                        sx={{ color: 'info.main', bgcolor: 'action.selected', '&:hover': { color: 'info.dark' } }}
                      >
                        <FormatListBulletedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {editingSection !== 'latex' ? (
                    <>
                      <Tooltip title="Search (Ctrl+F)">
                        <IconButton size="small" onClick={() => { setLatexSearchOpen(o => !o); setTimeout(() => latexSearchInputRef.current?.focus(), 50); }} sx={{ color: latexSearchOpen ? 'primary.main' : 'text.secondary' }}>
                          <SearchIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={latexFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}>
                        <IconButton size="small" onClick={() => setLatexFullscreen(f => !f)} sx={{ color: latexFullscreen ? 'primary.main' : 'text.secondary' }}>
                          {latexFullscreen ? <FullscreenExitIcon sx={{ fontSize: 16 }} /> : <FullscreenIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit LaTeX">
                        <IconButton size="small" onClick={() => startEditing('latex', currentLatex)} sx={{ color: 'warning.main' }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={hasBackup ? 'Revert to previous saved version' : 'No previous version available'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setRevertModalOpen(true)}
                            disabled={!hasBackup || reverting || latexSaving}
                            sx={{ color: hasBackup ? 'info.main' : 'text.disabled' }}
                          >
                            <RestoreIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Copy">
                        <IconButton size="small" onClick={() => handleCopy(currentLatex, 'LaTeX code')} sx={{ color: 'success.main' }}>
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Search (Ctrl+F)">
                        <IconButton size="small" onClick={() => { setLatexSearchOpen(o => !o); setTimeout(() => latexSearchInputRef.current?.focus(), 50); }} sx={{ color: latexSearchOpen ? 'primary.main' : 'text.secondary' }}>
                          <SearchIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={latexFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}>
                        <IconButton size="small" onClick={() => setLatexFullscreen(f => !f)} sx={{ color: latexFullscreen ? 'primary.main' : 'text.secondary' }}>
                          {latexFullscreen ? <FullscreenExitIcon sx={{ fontSize: 16 }} /> : <FullscreenIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Save">
                        <IconButton size="small" onClick={() => saveEdit()} sx={{ color: 'success.main' }}>
                          <SaveIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton size="small" onClick={() => cancelEdit()} sx={{ color: 'error.main' }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              )}
            </Box>
            {/* Search bar */}
            {latexSearchOpen && editorViewMode === 'latex' && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5,
                bgcolor: '#161b22', borderBottom: '1px solid rgba(255,255,255,0.08)',
                flexShrink: 0,
              }}>
                <TextField
                  inputRef={latexSearchInputRef}
                  size="small"
                  placeholder="Search..."
                  value={latexSearchTerm}
                  onChange={(e) => {
                    setLatexSearchTerm(e.target.value);
                    // In view mode, search live; in edit mode, wait for Enter
                    if (editingSection !== 'latex') handleLatexSearch(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (latexSearchCount === 0) {
                        // First Enter: trigger search
                        handleLatexSearch(latexSearchTerm);
                      } else {
                        // Subsequent Enter: navigate to next match
                        handleSearchNav(e.shiftKey ? 'prev' : 'next');
                      }
                    }
                    if (e.key === 'Escape') { closeLatexSearch(); }
                  }}
                  sx={{
                    flex: 1, maxWidth: 280,
                    '& .MuiInputBase-root': { bgcolor: '#0d1117', borderRadius: 1, height: 30 },
                    '& .MuiInputBase-input': { fontSize: '0.8rem', color: '#c9d1d9', py: 0.5, px: 1 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                {editingSection === 'latex' && (
                  <Tooltip title="Search (Enter)">
                    <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleLatexSearch(latexSearchTerm)} sx={{ color: 'primary.main' }}>
                      <SearchIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', minWidth: 50, textAlign: 'center', userSelect: 'none' }}>
                  {latexSearchTerm ? `${latexSearchCount > 0 ? latexSearchIndex + 1 : 0} / ${latexSearchCount}` : ''}
                </Typography>
                <Tooltip title="Previous (Shift+Enter)">
                  <span>
                    <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleSearchNav('prev')} disabled={latexSearchCount === 0}>
                      <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Next (Enter)">
                  <span>
                    <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleSearchNav('next')} disabled={latexSearchCount === 0}>
                      <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Close (Esc)">
                  <IconButton size="small" onClick={closeLatexSearch}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {/* Editor area */}
            <Box
              ref={latexEditorAreaRef}
              onMouseEnter={() => setFocusedPane('latex')}
              onMouseDown={() => setFocusedPane('latex')}
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                '&::-webkit-scrollbar': { width: 8 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 4 },
                ...(latexHandMode && editingSection !== 'latex' && { userSelect: 'none' }),
                ...(focusedPane === 'latex' && { boxShadow: 'inset 2px 0 0 0 rgba(33,150,243,0.4)' }),
              }}
            >
              {editorViewMode === 'latex' ? (
                <>
                  {editingSection === 'latex' ? (
                    latexSearchTerm && latexSearchCount > 0 ? (
                      /* Search active: render as highlighted contentEditable pre block */
                      <Box
                        className="search-highlight-backdrop"
                        contentEditable
                        suppressContentEditableWarning
                        onInput={(e) => {
                          // Preserve scroll + caret position across React's DOM rewrite
                          const scrollEl = latexEditorAreaRef.current;
                          const savedScroll = scrollEl ? scrollEl.scrollTop : 0;
                          const sel = window.getSelection();
                          let caretOffset = 0;
                          if (sel && sel.rangeCount > 0) {
                            const range = sel.getRangeAt(0).cloneRange();
                            range.setStart(e.currentTarget, 0);
                            caretOffset = range.toString().length;
                          }
                          setEditText(e.currentTarget.textContent || '');
                          requestAnimationFrame(() => {
                            if (scrollEl) scrollEl.scrollTop = savedScroll;
                            // Restore caret
                            const el = e.currentTarget;
                            if (!el || !sel) return;
                            const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
                            let remaining = caretOffset;
                            let node = walker.nextNode();
                            while (node) {
                              const len = node.nodeValue.length;
                              if (remaining <= len) {
                                const newRange = document.createRange();
                                newRange.setStart(node, remaining);
                                newRange.collapse(true);
                                sel.removeAllRanges();
                                sel.addRange(newRange);
                                return;
                              }
                              remaining -= len;
                              node = walker.nextNode();
                            }
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Tab') {
                            e.preventDefault();
                            document.execCommand('insertText', false, '  ');
                          }
                        }}
                        sx={{
                          p: 1.5, bgcolor: '#0d1117', minHeight: '100%',
                          fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", monospace',
                          fontSize: `${latexFontSize}rem`, lineHeight: 1.6,
                          color: '#c9d1d9', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          outline: 'none', caretColor: '#c9d1d9',
                          '& mark': { borderRadius: '2px', padding: '1px 2px' },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: (() => {
                            const escaped = editText
                              .replace(/&/g, '&amp;')
                              .replace(/</g, '&lt;')
                              .replace(/>/g, '&gt;');
                            const escapedTerm = latexSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                            const regex = new RegExp(`(${escapedTerm})`, 'gi');
                            let idx = 0;
                            return escaped.replace(regex, (m) => {
                              const isActive = idx === latexSearchIndex;
                              idx++;
                              const bg = isActive ? '#f5a623' : '#fef08a';
                              return `<mark data-search-match="${idx - 1}" style="background-color:${bg};color:#000;border-radius:2px;padding:1px 2px">${m}</mark>`;
                            });
                          })()
                        }}
                      />
                    ) : (
                      /* No search: plain textarea */
                      <TextField
                        fullWidth
                        multiline
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        sx={{
                          '& .MuiInputBase-root': { bgcolor: '#0d1117', borderRadius: 0, alignItems: 'flex-start' },
                          '& .MuiInputBase-input': { fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", monospace', fontSize: `${latexFontSize}rem`, lineHeight: 1.6, color: '#c9d1d9', p: 1.5 },
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                      />
                    )
                  ) : (
                    <Box onDoubleClick={() => startEditing('latex', currentLatex)} sx={{ p: 1.5, bgcolor: '#0d1117', minHeight: '100%', cursor: 'pointer' }}>
                      {jumpHighlight && !latexSearchTerm ? (
                        <Typography component="pre" sx={{ fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", monospace', fontSize: `${latexFontSize}rem`, lineHeight: 1.6, color: '#c9d1d9', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                          {currentLatex.substring(0, jumpHighlight.index)}
                          <Box component="mark" sx={{
                            background: 'linear-gradient(90deg, #f5a623 0%, #f5a62366 100%)',
                            color: '#000', px: '2px', borderRadius: '2px',
                            animation: 'jumpFlash 2s ease-out',
                            '@keyframes jumpFlash': {
                              '0%': { background: '#f5a623' },
                              '100%': { background: 'transparent', color: '#c9d1d9' },
                            },
                          }}>
                            {currentLatex.substring(jumpHighlight.index, jumpHighlight.index + jumpHighlight.length)}
                          </Box>
                          {currentLatex.substring(jumpHighlight.index + jumpHighlight.length)}
                        </Typography>
                      ) : (
                        <Typography component="pre" sx={{ fontFamily: '"Fira Code", "JetBrains Mono", "Consolas", monospace', fontSize: `${latexFontSize}rem`, lineHeight: 1.6, color: '#c9d1d9', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                          {latexSearchTerm && currentLatex ? latexSearchHighlight(currentLatex, latexSearchTerm).highlighted : currentLatex}
                        </Typography>
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>
                  {/* Loading / empty state */}
                  {!currentLatex && latexLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body1" color="text.secondary">Loading .tex file from server...</Typography>
                    </Box>
                  )}
                  {!currentLatex && !latexLoading && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      <Typography variant="body2">No .tex file found on server. Upload a .tex file via the Editor tab or save LaTeX content.</Typography>
                    </Alert>
                  )}
                  {/* Paper Title Card */}
                  <Card sx={{ mb: 3, borderLeft: '4px solid #1565c0' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                        {editingSection === 'title' ? (
                          <TextField
                            fullWidth
                            multiline
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            sx={{ '& .MuiInputBase-root': { fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.4 } }}
                          />
                        ) : (
                          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ lineHeight: 1.4 }}>
                            {currentLatexSections.title || 'NSCAT: A Multi-Vendor Firewall Compliance Framework with Triple-Strategy NIST SP 800-53 Mapping, Explainable AI, and SOC Orchestration'}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, mt: 0.5 }}>
                          {editingSection === 'title' ? (
                            <>
                              <Tooltip title="Save">
                                <IconButton size="small" onClick={() => {
                                  const oldTitle = currentLatexSections.title;
                                  if (oldTitle) {
                                    const newLatex = currentLatex.replace(`\\title{${oldTitle}}`, `\\title{${editText}}`);
                                    saveToServer(newLatex).then((saved) => {
                                      setSnackbar({ open: true, message: saved ? 'Title saved' : 'Save failed' });
                                      if (!saved) setCustomLatex(newLatex);
                                    });
                                  }
                                  setEditingSection(null);
                                  setEditText('');
                                }} sx={{ color: 'success.main' }}>
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" onClick={() => cancelEdit()} sx={{ color: 'error.main' }}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title="Edit Title">
                              <IconButton size="small" onClick={() => startEditing('title', currentLatexSections.title || '')} sx={{ color: 'warning.main' }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
                        Naeem Amjad, Attiq Ahmad, Narmeen Shafqat, Imran Rashid
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Department of Information Security, National University of Sciences & Technology (NUST), Islamabad, Pakistan
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
                        <Chip label="IEEE Format" size="small" color="primary" variant="outlined" />
                        <Chip label="8 Sections" size="small" variant="outlined" />
                        <Chip label="6 Firewall Vendors" size="small" variant="outlined" />
                        <Chip label="6 LLM Providers" size="small" variant="outlined" />
                        <Chip label="5 xAI Layers" size="small" variant="outlined" />
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Abstract Card */}
                  <Card sx={{ mb: 3, borderLeft: '4px solid #1565c0', cursor: editingSection === 'abstract' ? 'default' : 'pointer' }} onClick={() => { if (editingSection !== 'abstract') setAbstractExpanded(!abstractExpanded); }}>
                    <CardContent sx={{ pb: abstractExpanded ? 2 : '16px !important' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DescriptionIcon color="primary" />
                          <Typography variant="subtitle1" fontWeight={600}>Abstract</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label={customLatex ? 'Edited' : 'Outline'} size="small" variant="outlined" color="success" />
                          {abstractExpanded && editingSection !== 'abstract' && (
                            <>
                              <Tooltip title="Edit Abstract">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); startEditing('abstract', currentLatexSections.abstract || ''); }} sx={{ color: 'warning.main' }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy Abstract">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopy(getAbstractText(), 'Abstract'); }} sx={{ color: 'primary.main' }}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy Abstract LaTeX">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopy(currentLatexSections.abstract, 'Abstract LaTeX'); }} sx={{ color: 'success.main' }}>
                                  <CodeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {editingSection === 'abstract' && (
                            <>
                              <Tooltip title="Save">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); saveEdit(); }} sx={{ color: 'success.main' }}>
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); cancelEdit(); }} sx={{ color: 'error.main' }}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </Box>
                      {abstractExpanded && (
                        <Box sx={{ mt: 2 }}>
                          <Divider sx={{ mb: 2 }} />
                          {editingSection === 'abstract' ? (
                            <TextField
                              fullWidth
                              multiline
                              minRows={8}
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              variant="outlined"
                              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem', lineHeight: 1.85 } }}
                            />
                          ) : (
                            getAbstractText().split('\n\n').map((para, idx) => (
                              <Typography key={idx} variant="body2" color="text.secondary" paragraph sx={{ textAlign: 'justify', lineHeight: 1.85 }}>
                                {para}
                              </Typography>
                            ))
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* Keywords Card */}
                  <Card sx={{ mb: 3, borderLeft: '4px solid #1565c0', cursor: editingSection === 'keywords' ? 'default' : 'pointer' }} onClick={() => { if (editingSection !== 'keywords') setKeywordsExpanded(!keywordsExpanded); }}>
                    <CardContent sx={{ pb: keywordsExpanded ? 2 : '16px !important' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LabelIcon color="primary" />
                          <Typography variant="subtitle1" fontWeight={600}>Keywords</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {keywordsExpanded && editingSection !== 'keywords' && (
                            <>
                              <Tooltip title="Edit Keywords">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); startEditing('keywords', currentLatexSections.keywords || '\\begin{IEEEkeywords}\n\n\\end{IEEEkeywords}'); }} sx={{ color: 'warning.main' }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy Keywords">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopy(currentLatexSections.keywordsText || '', 'Keywords'); }} sx={{ color: 'primary.main' }}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {editingSection === 'keywords' && (
                            <>
                              <Tooltip title="Save">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); saveEdit(); }} sx={{ color: 'success.main' }}>
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); cancelEdit(); }} sx={{ color: 'error.main' }}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </Box>
                      {keywordsExpanded && (
                        <Box sx={{ mt: 2 }}>
                          <Divider sx={{ mb: 2 }} />
                          {editingSection === 'keywords' ? (
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="\\begin{IEEEkeywords}&#10;keyword1, keyword2, ...&#10;\\end{IEEEkeywords}"
                              sx={{
                                '& .MuiInputBase-root': { fontFamily: '"Fira Code", monospace', fontSize: '0.85rem', lineHeight: 1.6 },
                              }}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              {(currentLatexSections.keywordsText || 'Network Security, Configuration Auditing, Artificial Intelligence, Large Language Models, Multi-Vendor Firewall, NIST SP 800-53 Rev 5, Retrieval-Augmented Generation, Explainable AI, Canonical Data Model, Security Operations Center, Compliance Automation, SIEM, SOAR')
                                .split(',').map((kw) => kw.trim()).filter(Boolean).map((kw) => (
                                <Chip key={kw} label={kw} size="small" color="primary" variant="outlined" />
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* Section Cards */}
                  {(currentLatexSections.sections || []).map((section, idx) => {
                    const key = section.key;
                    const isEditing = editingSection === key;
                    return (
                      <Card key={idx} sx={{ mb: 2, borderLeft: '4px solid', borderColor: 'success.main', cursor: isEditing ? 'default' : 'pointer' }} onClick={() => { if (!isEditing) toggleSection(idx); }}>
                        <CardContent sx={{ pb: expandedSection === idx ? 2 : '16px !important' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Chip label={section.num} size="small" sx={{ fontWeight: 700, minWidth: 32, fontFamily: 'monospace' }} color="primary" variant="outlined" />
                              <Typography variant="subtitle1" fontWeight={600}>{section.title}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label={customLatex ? 'Edited' : 'Outline'} size="small" variant="outlined" color="success" />
                              {expandedSection === idx && !isEditing && (
                                <>
                                  <Tooltip title={`Edit Section ${section.num}`}>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); startEditing(key, currentLatexSections[key] || ''); }} sx={{ color: 'warning.main' }}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={`Copy Section ${section.num}`}>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopy(getSectionText(key), section.title); }} sx={{ color: 'success.main' }}>
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={`Copy Section ${section.num} LaTeX`}>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopy(currentLatexSections[key], `Section ${section.num} LaTeX`); }} sx={{ color: 'primary.main' }}>
                                      <CodeIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              {isEditing && (
                                <>
                                  <Tooltip title="Save">
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); saveEdit(); }} sx={{ color: 'success.main' }}>
                                      <SaveIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cancel">
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); cancelEdit(); }} sx={{ color: 'error.main' }}>
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </Box>
                          {expandedSection === idx && (
                            <Box sx={{ mt: 2 }}>
                              <Divider sx={{ mb: 2 }} />
                              {isEditing ? (
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={10}
                                  value={editText}
                                  onChange={(e) => setEditText(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  variant="outlined"
                                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem', lineHeight: 1.85 } }}
                                />
                              ) : getSubsections(section.latex).length === 0 && (
                                getSectionText(key).split('\n\n').map((para, pIdx) => (
                                  <Typography key={pIdx} variant="body2" color="text.secondary" paragraph sx={{ textAlign: 'justify', lineHeight: 1.85 }}>
                                    {para}
                                  </Typography>
                                ))
                              )}
                              {getSubsections(section.latex).length > 0 && !isEditing && (
                                <Box sx={{ mt: 2, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    Subsections
                                  </Typography>
                                  {getSubsections(section.latex).map((sub, sIdx) => {
                                    const subKey = `${idx}-${sIdx}`;
                                    const isSubExpanded = expandedSubsections[subKey];
                                    const isSubEditing = editingSubsection?.sectionIdx === idx && editingSubsection?.subIdx === sIdx;
                                    return (
                                      <Box key={sIdx} sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                                        <Box
                                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.75, cursor: isSubEditing ? 'default' : 'pointer', bgcolor: isSubExpanded ? 'action.hover' : 'transparent', '&:hover': { bgcolor: 'action.hover' } }}
                                          onClick={(e) => { e.stopPropagation(); if (!isSubEditing) toggleSubsection(idx, sIdx); }}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip label={`${section.num}.${sIdx + 1}`} size="small" sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20, fontFamily: 'monospace' }} variant="outlined" color="info" />
                                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>{sub.title}</Typography>
                                          </Box>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {isSubExpanded && !isSubEditing && (
                                              <>
                                                <Tooltip title="Edit Subsection">
                                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); startEditingSubsection(idx, sIdx, sub.latex); }} sx={{ p: 0.25, color: 'warning.main' }}>
                                                    <EditIcon sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Copy Text">
                                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopy(stripLatex(sub.latex), sub.title); }} sx={{ p: 0.25, color: 'success.main' }}>
                                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Copy LaTeX">
                                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopy(sub.latex, `${sub.title} LaTeX`); }} sx={{ p: 0.25, color: 'primary.main' }}>
                                                    <CodeIcon sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Tooltip>
                                              </>
                                            )}
                                            {isSubEditing && (
                                              <>
                                                <Tooltip title="Save">
                                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); saveSubsectionEdit(idx, sIdx); }} sx={{ p: 0.25, color: 'success.main' }}>
                                                    <SaveIcon sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Cancel">
                                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); cancelSubsectionEdit(); }} sx={{ p: 0.25, color: 'error.main' }}>
                                                    <CloseIcon sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Tooltip>
                                              </>
                                            )}
                                          </Box>
                                        </Box>
                                        {isSubExpanded && (
                                          <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }} onClick={(e) => e.stopPropagation()}>
                                            {isSubEditing ? (
                                              <TextField
                                                fullWidth
                                                multiline
                                                minRows={5}
                                                value={editSubText}
                                                onChange={(e) => setEditSubText(e.target.value)}
                                                variant="outlined"
                                                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8rem', lineHeight: 1.75 } }}
                                              />
                                            ) : (
                                              stripLatex(sub.latex).split('\n\n').map((para, pIdx) => (
                                                <Typography key={pIdx} variant="body2" color="text.secondary" paragraph sx={{ textAlign: 'justify', lineHeight: 1.75, fontSize: '0.8rem', mb: 1 }}>
                                                  {para}
                                                </Typography>
                                              ))
                                            )}
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>

          {/* Draggable divider */}
          <Box
            onMouseDown={(e) => {
              e.preventDefault();
              isDraggingDivider.current = true;
              setIsDraggingActive(true);
              document.body.style.cursor = 'col-resize';
              document.body.style.userSelect = 'none';
            }}
            sx={{
              width: 6,
              cursor: 'col-resize',
              bgcolor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              position: 'relative',
              zIndex: 10,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 2,
                bgcolor: 'divider',
                transition: 'background-color 0.15s, width 0.15s',
              },
              '&:hover::before': {
                width: 4,
                bgcolor: 'primary.main',
              },
            }}
          />

          {/* Right: PDF Preview */}
          <Box sx={{ width: `${100 - editorWidthPercent}%`, minWidth: 0, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* PDF toolbar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0, position: 'sticky', top: 0, zIndex: 10 }}>
              <Tooltip title="Back to Home">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (isDirty && !window.confirm('You have unsaved changes to the .tex file. Leave without saving?')) return;
                    navigate('/');
                  }}
                  aria-label="Back to Home"
                  sx={{ p: 0.5, color: 'text.secondary' }}
                >
                  <HomeIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {pdfCompiling && <CircularProgress size={14} sx={{ mr: 0.5 }} />}
                {pdfUrl && (
                  <>
                    <ShowSearchPopover>
                      {(props) => (
                        <Tooltip title="Search in PDF">
                          <IconButton size="small" onClick={props.onClick} sx={{ p: 0.5 }}>
                            <SearchIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </ShowSearchPopover>
                    <Tooltip title="Bookmarks">
                      <IconButton size="small" onClick={() => setBookmarksOpen((p) => !p)} sx={{ p: 0.5 }}>
                        <BookmarkIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <PdfViewBgButton mode={pdfViewBg} onCycle={cyclePdfViewBg} />
                    <Tooltip title="Zoom out (−5%)">
                      <IconButton size="small" onClick={() => stepZoom(-0.05)} sx={{ p: 0.5 }}>
                        <RemoveIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <CurrentScale>
                      {(props) => (
                        <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'center', userSelect: 'none' }}>
                          {Math.round(props.scale * 100)}%
                        </Typography>
                      )}
                    </CurrentScale>
                    <Tooltip title="Zoom in (+5%)">
                      <IconButton size="small" onClick={() => stepZoom(0.05)} sx={{ p: 0.5 }}>
                        <AddIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ width: 1, height: 20, bgcolor: 'rgba(0,0,0,0.12)', mx: 0.5 }} />
                  </>
                )}
                <Tooltip title={pdfCompiling ? 'Compiling...' : pdfUrl ? 'Recompile PDF' : 'Compile PDF'}>
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => compilePdf(true)}
                      disabled={pdfCompiling}
                      sx={{
                        p: 0.5,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': { bgcolor: 'primary.dark' },
                        '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                      }}
                    >
                      {pdfCompiling ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
                {pdfUrl && (
                  <>
                    <SwitchSelectionMode mode={SelectionMode.Hand}>
                      {(handProps) => (
                        <SwitchSelectionMode mode={SelectionMode.Text}>
                          {(textProps) => {
                            const isHand = pdfSelectionMode === SelectionMode.Hand;
                            selectionModeHandlersRef.current = { hand: handProps.onClick, text: textProps.onClick };
                            return (
                              <Tooltip title={isHand ? 'Switch to Text Selection tool' : 'Switch to Hand tool (drag to pan PDF)'}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (isHand) {
                                      textProps.onClick();
                                      setPdfSelectionMode(SelectionMode.Text);
                                    } else {
                                      handProps.onClick();
                                      setPdfSelectionMode(SelectionMode.Hand);
                                    }
                                  }}
                                  sx={{
                                    color: 'primary.main',
                                    bgcolor: 'action.selected',
                                    '&:hover': { color: 'primary.dark' },
                                  }}
                                >
                                  {isHand
                                    ? <Box component="span" sx={{ display: 'inline-flex', width: 16, height: 16, '& svg': { width: 16, height: 16 } }}><TextSelectionIcon /></Box>
                                    : <PanToolIcon sx={{ fontSize: 16 }} />}
                                </IconButton>
                              </Tooltip>
                            );
                          }}
                        </SwitchSelectionMode>
                      )}
                    </SwitchSelectionMode>
                    <Tooltip title={pdfFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen PDF view'}>
                      <IconButton
                        size="small"
                        onClick={() => setPdfFullscreen(f => !f)}
                        sx={{ color: pdfFullscreen ? 'primary.main' : 'text.secondary', '&:hover': { color: 'primary.main' } }}
                      >
                        {pdfFullscreen ? <FullscreenExitIcon sx={{ fontSize: 16 }} /> : <FullscreenIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open PDF in new tab (full view, searchable, printable)">
                      <IconButton
                        size="small"
                        onClick={() => window.open(pdfUrl, '_blank')}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download PDF">
                      <span>
                        <IconButton
                          size="small"
                          component="a"
                          href={pdfUrl}
                          download="NSCAT_Research_Paper.pdf"
                          sx={{ p: 0.5 }}
                        >
                          <GetAppIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
                <Tooltip title="Download standalone Highlights Word document (.docx) — auto-generated from the current paper.tex. Elsevier requires Word format; open in Microsoft Word to verify before uploading at Editorial Manager">
                  <span>
                    <IconButton
                      size="small"
                      onClick={async () => {
                        try {
                          const blob = await researchPaperService.downloadHighlightsPdf(PAPER_ID);
                          const docxBlob = new Blob([blob], {
                            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          });
                          const url = window.URL.createObjectURL(docxBlob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'highlights.docx';
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(url);
                          setSnackbar({ open: true, message: 'highlights.docx downloaded — open in Word to verify, then upload to Editorial Manager (Highlights field)' });
                        } catch (err) {
                          setSnackbar({ open: true, message: `Highlights download failed: ${err?.response?.data?.error || err.message || 'unknown error'}` });
                        }
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <FormatListBulletedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Download Project">
                  <span>
                    <IconButton
                      size="small"
                      onClick={async () => {
                        try {
                          const blob = await researchPaperService.downloadZip(PAPER_ID);
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'NSCAT_Paper_1_Overleaf.zip';
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                          setSnackbar({ open: true, message: 'LaTeX project downloaded — upload to Overleaf' });
                        } catch {
                          setSnackbar({ open: true, message: 'Download failed' });
                        }
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <FolderZipIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            {/* PDF error */}
            {pdfError && (
              <Alert severity="error" sx={{ mx: 1, mt: 0.5 }} onClose={() => setPdfError(null)}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '0.7rem' }}>{pdfError}</Typography>
              </Alert>
            )}
            {/* PDF viewer */}
            {pdfUrl ? (
              <Box
                ref={pdfContainerRef}
                onMouseEnter={() => setFocusedPane('pdf')}
                onMouseDown={() => setFocusedPane('pdf')}
                onDoubleClick={() => {
                  const handlers = selectionModeHandlersRef.current;
                  if (pdfSelectionMode === SelectionMode.Hand) {
                    handlers.text?.();
                    setPdfSelectionMode(SelectionMode.Text);
                  } else {
                    handlers.hand?.();
                    setPdfSelectionMode(SelectionMode.Hand);
                  }
                }}
                data-pdf-fs={pdfFullscreen ? '' : undefined}
                data-pdf-bg={pdfViewBg}
                sx={{
                  bgcolor: PDF_VIEW_BACKGROUNDS[pdfViewBg].color,
                  '--rpv-default-layout__body-background-color': PDF_VIEW_BACKGROUNDS[pdfViewBg].color,
                  '--rpv-core__inner-page-background-color': PDF_VIEW_BACKGROUNDS[pdfViewBg].color,
                  '--rpv-core__doc-loading-background-color': PDF_VIEW_BACKGROUNDS[pdfViewBg].color,
                  ...(pdfFullscreen
                    ? {
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        width: '100vw', height: '100vh', zIndex: 1300,
                        overflow: 'hidden',
                        pl: `${PDF_FS_RAIL_PX}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        '& .rpv-core__viewer': { flex: '1 1 auto', minHeight: 0, height: '100%' },
                      }
                    : {
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        pointerEvents: isDraggingActive ? 'none' : 'auto',
                        ...(focusedPane === 'pdf' && { boxShadow: 'inset 2px 0 0 0 rgba(33,150,243,0.4)' }),
                      }),
                }}
              >
                <Drawer
                  anchor="left"
                  open={bookmarksOpen}
                  onClose={() => setBookmarksOpen(false)}
                  PaperProps={{ sx: { width: 280, p: 1 } }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Bookmarks</Typography>
                  <Bookmarks />
                </Drawer>
                <style>{PDF_VIEW_BG_CSS}</style>
                <Worker workerUrl={workerUrl}>
                  <Viewer
                    fileUrl={pdfUrl}
                    plugins={[defaultLayoutPluginInstance, selectionModePluginInstance, zoomPluginInstance, searchPluginInstance, bookmarkPluginInstance]}
                    defaultScale={1.5}
                    onZoom={(e) => setPdfScale(e.scale)}
                    setRenderRange={(visiblePagesRange) => ({
                      startPage: 0,
                      endPage: visiblePagesRange.numPages - 1,
                    })}
                    onDocumentLoad={() => {
                      const target = pdfContainerRef.current;
                      if (!target) return;
                      const tryCentre = () => {
                        const inner = target.querySelector('.rpv-core__inner-pages');
                        if (!inner) return false;
                        if (inner.scrollWidth <= inner.clientWidth) return true;
                        inner.scrollLeft = (inner.scrollWidth - inner.clientWidth) / 2;
                        return true;
                      };
                      if (tryCentre()) return;
                      const ob = new MutationObserver(() => {
                        if (tryCentre()) ob.disconnect();
                      });
                      ob.observe(target, { childList: true, subtree: true });
                      setTimeout(() => ob.disconnect(), 3000); // safety net
                    }}
                  />
                </Worker>

                {/* Vertical control rail — fullscreen PDF only.
                    Docked to the top-left corner, inside the left gutter, so it
                    does not cover the page. The page area runs to the right edge.
                    Auto-hides after 3 s of inactivity and reappears on activity. */}
                {pdfFullscreen && (
                  <Box
                    onMouseEnter={() => showPdfControls()}
                    sx={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      zIndex: 1400,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: PDF_FS_RAIL_PX,
                      gap: 0.25,
                      px: 0.25,
                      py: 0.75,
                      borderRadius: '0 10px 10px 0',
                      bgcolor: 'rgba(15, 23, 42, 0.92)',
                      backdropFilter: 'blur(8px)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
                      opacity: pdfControlsVisible ? 1 : 0,
                      transform: pdfControlsVisible ? 'translateX(0)' : 'translateX(-100%)',
                      pointerEvents: pdfControlsVisible ? 'auto' : 'none',
                      transition: 'opacity 0.25s ease, transform 0.25s ease',
                    }}
                  >
                    <ShowSearchPopover>
                      {(props) => (
                        <Tooltip title="Search in PDF" placement="right">
                          <IconButton size="small" onClick={props.onClick} sx={{ color: 'common.white' }}>
                            <SearchIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </ShowSearchPopover>
                    <PdfViewBgButton mode={pdfViewBg} onCycle={cyclePdfViewBg} placement="right" onDark />
                    <Box sx={{ width: 22, height: 1, bgcolor: 'rgba(255,255,255,0.2)', my: 0.25 }} />
                    <Tooltip title="Zoom out (−5%)" placement="right">
                      <IconButton size="small" onClick={() => stepZoom(-0.05)} sx={{ color: 'common.white' }}>
                        <RemoveIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ width: '100%', textAlign: 'center', color: 'common.white', fontSize: '0.7rem', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                      <CurrentScale>
                        {(props) => `${Math.round(props.scale * 100)}%`}
                      </CurrentScale>
                    </Box>
                    <Tooltip title="Zoom in (+5%)" placement="right">
                      <IconButton size="small" onClick={() => stepZoom(0.05)} sx={{ color: 'common.white' }}>
                        <AddIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Fit to width" placement="right">
                      <IconButton size="small" onClick={() => { zoomTo(1.6); setPdfScale(1.6); }} sx={{ color: 'common.white' }}>
                        <FitScreenIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ width: 22, height: 1, bgcolor: 'rgba(255,255,255,0.2)', my: 0.25 }} />
                    <Tooltip title={pdfCompiling ? 'Refetching...' : 'Refetch PDF'} placement="right">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => refetchPdf()}
                          disabled={pdfCompiling}
                          sx={{ color: 'common.white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' } }}
                        >
                          {pdfCompiling ? <CircularProgress size={18} sx={{ color: 'common.white' }} /> : <CloudDownloadIcon sx={{ fontSize: 20 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {/* Same action as the toolbar's Recompile button: compiles the saved paper.tex. */}
                    <Tooltip title={pdfCompiling ? 'Compiling...' : 'Recompile PDF'} placement="right">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => compilePdf(true)}
                          disabled={pdfCompiling}
                          sx={{
                            color: 'primary.contrastText',
                            bgcolor: 'primary.main',
                            '&:hover': { bgcolor: 'primary.dark' },
                            '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.35)' },
                          }}
                        >
                          {pdfCompiling ? <CircularProgress size={18} sx={{ color: 'common.white' }} /> : <RefreshIcon sx={{ fontSize: 20 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Exit fullscreen (Esc)" placement="right">
                      <IconButton size="small" onClick={() => setPdfFullscreen(false)} sx={{ color: 'common.white' }}>
                        <FullscreenExitIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            ) : !pdfCompiling && !pdfError ? (
              <Box
                sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', cursor: 'pointer' }}
                onClick={() => compilePdf()}
              >
                <PictureAsPdfIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">Click to Compile</Typography>
                <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />} sx={{ textTransform: 'none', mt: 1 }}>Compile Now</Button>
              </Box>
            ) : null}
          </Box>
          </Box>
        </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* File deletion confirmation dialog */}
      <Dialog
        open={deleteConfirm.open}
        onClose={cancelDelete}
        aria-labelledby="delete-file-title"
        aria-describedby="delete-file-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="delete-file-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Delete file?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-file-description" sx={{ mb: 1 }}>
            This will permanently delete the file from the paper project. This action cannot be undone.
          </DialogContentText>
          {deleteConfirm.filePath && (
            <Box sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              wordBreak: 'break-all',
              border: '1px solid',
              borderColor: 'divider',
            }}>
              {deleteConfirm.filePath}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} autoFocus>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" startIcon={<DeleteIcon />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Outline / Jump-to-section menu */}
      <Menu
        anchorEl={outlineAnchor}
        open={!!outlineAnchor}
        onClose={() => {
          setOutlineAnchor(null);
          setSubOutlineAnchor(null);
          setSubOutlineSection(null);
          hoveringParentRef.current = false;
          hoveringSubRef.current = false;
          cancelCloseSubMenu();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { maxHeight: 400, minWidth: 280 },
          onMouseEnter: handleParentEnter,
          onMouseLeave: handleParentLeave,
        }}
      >
        <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Jump to section in LaTeX source
          </Typography>
        </Box>
        {outlineItems.map((item) => {
          const hasSubs = item.subsections && item.subsections.length > 0;
          return (
            <MenuItem
              key={`${item.label}-${item.index}`}
              onClick={() => { jumpToSourceLine(item.index, item.length); }}
              onMouseEnter={(e) => {
                cancelCloseSubMenu();
                if (hasSubs) {
                  setSubOutlineAnchor(e.currentTarget);
                  setSubOutlineSection(item);
                }
                // Hovering a parent item without subsections does NOT close the
                // current sub-menu — matches macOS Finder cascading behavior.
              }}
              sx={{ fontSize: '0.85rem', py: 0.75, pr: hasSubs ? 0.5 : 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box component="span" sx={{ flex: 1 }}>{item.label}</Box>
              {hasSubs && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    cancelCloseSubMenu();
                    setSubOutlineAnchor(e.currentTarget.closest('li') || e.currentTarget);
                    setSubOutlineSection(item);
                  }}
                  sx={{ p: 0.25, color: 'text.secondary' }}
                  aria-label={`Show subsections of ${item.label}`}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Cascading sub-section menu (Popper — no backdrop, no click interception) */}
      <Popper
        anchorEl={subOutlineAnchor}
        open={!!subOutlineAnchor && !!subOutlineSection}
        placement="right-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        sx={{ zIndex: 1400 }}
      >
        <Paper
          elevation={8}
          sx={{ maxHeight: 400, minWidth: 240, overflow: 'auto' }}
          onMouseEnter={handleSubEnter}
          onMouseLeave={handleSubLeave}
        >
          {subOutlineSection && (
            <>
              <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {subOutlineSection.label}
                </Typography>
              </Box>
              <MenuList dense disablePadding>
                <MenuItem
                  onClick={() => {
                    jumpToSourceLine(subOutlineSection.index, subOutlineSection.length);
                    setSubOutlineAnchor(null);
                    setSubOutlineSection(null);
                    setOutlineAnchor(null);
                  }}
                  sx={{ fontSize: '0.8rem', py: 0.75, fontStyle: 'italic', color: 'text.secondary' }}
                >
                  ↑ Jump to section start
                </MenuItem>
                {subOutlineSection.subsections.map((sub) => (
                  <MenuItem
                    key={`${sub.label}-${sub.index}`}
                    onClick={() => {
                      jumpToSourceLine(sub.index, sub.length);
                      setSubOutlineAnchor(null);
                      setSubOutlineSection(null);
                      setOutlineAnchor(null);
                    }}
                    sx={{ fontSize: '0.85rem', py: 0.75, pl: 2 }}
                  >
                    {sub.label}
                  </MenuItem>
                ))}
              </MenuList>
            </>
          )}
        </Paper>
      </Popper>

      {/* Revert confirmation dialog */}
      <Dialog
        open={revertModalOpen}
        onClose={() => !reverting && setRevertModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestoreIcon color="info" />
          Revert to previous saved version?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            This will replace the current <code>paper.tex</code> with the version saved before your last edit.
          </DialogContentText>
          {backupModified && (
            <DialogContentText variant="caption" sx={{ color: 'text.secondary' }}>
              Previous version timestamp: {new Date(backupModified).toLocaleString()}
            </DialogContentText>
          )}
          <DialogContentText sx={{ mt: 1.5, color: 'warning.main', fontSize: '0.85rem' }}>
            The current content will become the new backup, so you can undo this revert by clicking Revert again.
            The PDF will be recompiled automatically.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertModalOpen(false)} disabled={reverting}>
            Cancel
          </Button>
          <Button
            onClick={handleRevert}
            color="warning"
            variant="contained"
            startIcon={reverting ? <CircularProgress size={14} color="inherit" /> : <RestoreIcon />}
            disabled={reverting}
          >
            {reverting ? 'Reverting…' : 'Revert and Recompile'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const researchPaperCopyText = 'See the Research Paper page for full content.';
export { researchPaperCopyText };
export default ResearchPaper;
